/**
 * Authenticated chat view. Given an identity + authenticated ChatClient,
 * this owns the ConversationManager lifecycle, polls for invitations +
 * incoming ciphertext, and renders a three-zone layout:
 *
 *   - Topbar:       brand + protocol + identity tag + install prompt
 *   - Sidebar:      profile, user search, conversation list, matchmaking
 *   - Main panel:   empty lobby OR active conversation (header, bubbles,
 *                   composer, optional ActiveGame)
 *
 * Conversation names + member lists live in ConversationManager meta
 * storage (sealed under the user's passphrase). MemberDirectory hydrates
 * userId → profile labels on demand.
 */
import { fromBase64Url, toBase64Url, type ConversationId, type UserId } from '@chat/protocol';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from 'react';
import type { KeyStore } from '@chat/crypto';
import type { ChatClient } from '../client/api.js';
import { AttachmentService, type AttachmentReference } from '../client/attachments.js';
import { maybeDownscaleImage } from '../client/image-downscale.js';
import type { BackupBundle } from '../client/backup.js';
import {
  ConversationManager,
  type GameScore,
  type ReactionMap,
  type StoredMessage,
} from '../client/conversation.js';
import type { Identity } from '../client/identity.js';
import { MemberDirectory, shortId } from '../client/member-directory.js';
import { useUserSearch } from '../client/use-user-search.js';
import {
  decodePayload,
  encodePayload,
  type ChatPayload,
  type GameMessage,
} from '../client/payload.js';
import { PushService } from '../client/push.js';
import { isVideoRecordingAvailable, VideoRecorder } from '../client/video-recorder.js';
import { isVoiceRecordingAvailable, VoiceRecorder } from '../client/voice-recorder.js';
import { applyMessage, startSession, type GameSessionState } from '../games/reducer.js';
import { gameRegistry } from '../games/registry.js';
import { Sidebar } from './Sidebar.js';
import { VideoCallPanel } from './VideoCallPanel.js';

export interface ChatProps {
  readonly identity: Identity;
  readonly client: ChatClient;
  readonly store: KeyStore;
  readonly token: string;
  /** How often to refresh message history (DO storage, cheap). */
  readonly pollIntervalMs?: number;
  /** How often to drain the invitation inbox (KV list, expensive). Default: pollIntervalMs × 10. */
  readonly invitationPollIntervalMs?: number;
  /**
   * Export every key/value in the raw IndexedDB keystore as a
   * backup bundle. Owned by App.tsx (which holds the raw store);
   * the sidebar wires this to a "download backup" button.
   */
  readonly onExportBackup?: () => Promise<BackupBundle>;
}

interface LocalMessage {
  readonly direction: 'sent' | 'received';
  readonly payload: ChatPayload;
  readonly at: number;
  readonly senderUserId?: UserId;
  /**
   * Server-assigned sequence for this envelope. Used as the stable
   * identifier other messages (reactions, replies, deletions)
   * reference. Absent only for the brief window while a sent
   * message is awaiting the server's ack.
   */
  readonly sequence?: number;
  /** Object URL created locally when we've downloaded + decrypted the blob. */
  readonly attachmentObjectUrl?: string;
  /**
   * Aggregated reactions, mirroring the server-side persistent copy.
   * Keyed by emoji; value is the list of userIds who reacted with
   * that emoji. Empty / absent means no reactions yet.
   */
  readonly reactions?: ReactionMap;
  /**
   * Tombstone flag. True iff a deletion envelope from the original
   * sender has been received. The bubble renders "this message was
   * deleted" instead of the original payload.
   */
  readonly deleted?: true;
}

/** Re-emit typing no more often than this while actively composing. */
const TYPING_EMIT_THROTTLE_MS = 3_000;
/** Typing pills disappear this long after the last refresh from a peer. */
const TYPING_EXPIRY_MS = 7_000;

function storedToLocal(m: StoredMessage): LocalMessage {
  const payload = m.payload as ChatPayload;
  return {
    direction: m.direction,
    payload,
    at: m.at,
    sequence: m.sequence,
    ...(m.senderUserId ? { senderUserId: m.senderUserId } : {}),
    ...(m.reactions && Object.keys(m.reactions).length > 0 ? { reactions: m.reactions } : {}),
    ...(m.deleted ? { deleted: true as const } : {}),
  };
}

export function Chat(props: ChatProps) {
  const {
    identity,
    client,
    store,
    token,
    pollIntervalMs = 3000,
    invitationPollIntervalMs = pollIntervalMs * 10,
    onExportBackup,
  } = props;
  const [manager, setManager] = useState<ConversationManager | undefined>();
  const [attachments, setAttachments] = useState<AttachmentService | undefined>();
  const [ready, setReady] = useState(false);
  const [conversations, setConversations] = useState<readonly ConversationId[]>([]);
  const [selected, setSelected] = useState<ConversationId | undefined>();
  const [inviteTarget, setInviteTarget] = useState('');
  const [draft, setDraft] = useState('');
  const [pendingFile, setPendingFile] = useState<File | undefined>();
  const [messages, setMessages] = useState<Map<ConversationId, LocalMessage[]>>(new Map());
  const [error, setError] = useState<string | undefined>();
  const [pushStatus, setPushStatus] = useState<'idle' | 'granted' | 'denied' | 'unsupported'>(
    'idle',
  );
  // Reflect the real browser-side push state on mount. Without this
  // pushStatus snaps back to 'idle' on every reload even though the
  // browser is still subscribed and notifications are working.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (typeof Notification === 'undefined') {
        if (!cancelled) setPushStatus('unsupported');
        return;
      }
      if (Notification.permission === 'denied') {
        if (!cancelled) setPushStatus('denied');
        return;
      }
      if (Notification.permission !== 'granted') return;
      // Permission granted — confirm a subscription actually exists
      // (the user may have revoked at the OS level since the last
      // visit). Treat any failure as "idle so the user can re-grant".
      try {
        if (!('serviceWorker' in navigator)) return;
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        const sub = await reg?.pushManager.getSubscription();
        if (!cancelled && sub) setPushStatus('granted');
      } catch {
        // ignore — UI stays at 'idle' and the user can re-enable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [gameSessions, setGameSessions] = useState<Map<ConversationId, GameSessionState<unknown>>>(
    new Map(),
  );
  // Keyed by `${conversationId}:${engineId}` so multiple games per
  // conversation stay independent. Hydrated lazily on demand.
  const [gameScores, setGameScores] = useState<Map<string, GameScore>>(new Map());
  const [matchmakingStatus, setMatchmakingStatus] = useState<'idle' | 'searching' | 'waiting'>(
    'idle',
  );
  /**
   * Bumped every time the user sends a message from the composer.
   * MessageScroll watches this and force-snaps to the bottom when it
   * changes, overriding any scroll-up state. Separate from `messages`
   * so receives don't yank a user who's reading history.
   */
  const [forceScrollToken, setForceScrollToken] = useState(0);
  /**
   * When set, the composer displays a "replying to …" strip and
   * includes replyToSequence on the next outgoing message. Cleared
   * after a successful send or on explicit cancel.
   */
  const [replyToSequence, setReplyToSequence] = useState<number | undefined>(undefined);
  /**
   * Search-jump target — set when the user clicks a message-search
   * hit in the sidebar. The token bumps on every selection so jumping
   * to the same sequence twice still re-triggers the scroll/flash.
   */
  const [scrollTo, setScrollTo] = useState<{ sequence: number; token: number } | undefined>(
    undefined,
  );
  /**
   * Set when the user picks Forward on a bubble. Carries the source
   * message so the picker can render a preview and the dispatch
   * re-sends the same text/attachment payload into a chosen
   * conversation. Cleared after a successful forward or on cancel.
   */
  const [forwardingMessage, setForwardingMessage] = useState<LocalMessage | undefined>(undefined);
  /**
   * Image attachment opened full-screen via the lightbox overlay.
   * Carries the already-decrypted blob URL + filename so the
   * download button can reuse them. Cleared on close / overlay
   * click.
   */
  const [lightbox, setLightbox] = useState<{ src: string; filename: string } | undefined>(
    undefined,
  );
  const [recordingState, setRecordingState] = useState<'idle' | 'recording' | 'uploading'>('idle');
  const [recordingMode, setRecordingMode] = useState<'voice' | 'video'>('voice');
  const [recordingDuration, setRecordingDuration] = useState(0);
  const voiceRecorderRef = useRef<VoiceRecorder | undefined>(undefined);
  const videoRecorderRef = useRef<VideoRecorder | undefined>(undefined);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);
  /**
   * Per-conversation "who is currently typing" state. Value is the
   * ms-epoch that the typing pill should disappear. Refreshed each
   * time a typing envelope arrives; pruned by a ticking effect.
   */
  const [typingByConv, setTypingByConv] = useState<Map<ConversationId, Map<UserId, number>>>(
    new Map(),
  );
  /**
   * Per-peer read receipts. Key: conversationId → userId →
   * highest-sequence read. Used to render "seen" marks on own
   * messages. Kept in-memory only for this session — cheap to
   * re-acquire because peers re-emit on every read.
   */
  const [readReceipts, setReadReceipts] = useState<Map<ConversationId, Map<UserId, number>>>(
    new Map(),
  );
  /**
   * Debounce timestamp for outbound typing envelopes, per
   * conversation. We only re-emit once per TYPING_EMIT_THROTTLE_MS
   * of keystrokes so an active typer doesn't flood the network.
   */
  const lastTypingEmitRef = useRef<Map<ConversationId, number>>(new Map());
  const sinceRef = useRef<Map<ConversationId, number>>(new Map());
  const gameSessionsRef = useRef(gameSessions);
  gameSessionsRef.current = gameSessions;
  // Mirror `selected` into a ref so the poll callback can read the
  // up-to-date value without re-subscribing on every selection change.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // Composer-draft persistence. Cleared by handleSend; auto-saved
  // 500ms after the last keystroke. Tracks the conversation id we
  // last loaded a draft for so a quick conversation-switch doesn't
  // race a pending save.
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const draftLoadedForRef = useRef<ConversationId | undefined>(undefined);

  // When the selected conversation changes, hydrate its persisted
  // draft into the composer. Skip if we just loaded the same id
  // (e.g. React strict-mode double mount) and skip across the
  // initial nothing-selected → conversation switch by clearing.
  useEffect(() => {
    if (!manager) return;
    if (!selected) {
      draftLoadedForRef.current = undefined;
      setDraft('');
      return;
    }
    if (draftLoadedForRef.current === selected) return;
    let cancelled = false;
    void (async () => {
      const text = await manager.loadDraft(selected);
      if (cancelled) return;
      draftLoadedForRef.current = selected;
      setDraft(text);
    })();
    return () => {
      cancelled = true;
    };
  }, [manager, selected]);

  // Debounced save: 500ms after the last keystroke, persist whatever
  // is in the composer. We persist the empty string explicitly so a
  // user clearing a draft also clears the stored copy.
  useEffect(() => {
    if (!manager || !selected) return;
    if (draftLoadedForRef.current !== selected) return;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      void manager.saveDraft(selected, draft);
    }, 500);
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, [manager, selected, draft]);

  const directory = useMemo(
    // Pass the sealed keystore so the directory can hydrate known
    // profiles on boot. Without this, the first render of the lobby
    // falls back to shortId for every peer until /users/by-ids
    // round-trips; with it, returning users see last-known display
    // names immediately and we re-fetch only for stale or unknown
    // entries.
    () => new MemberDirectory(client, () => token, { store }),
    [client, token, store],
  );

  useEffect(() => {
    // Seed the directory with our own userId so the bubble label for
    // sent messages resolves instantly. Profile (username/displayName)
    // hydrates via the first batch request triggered by label().
    directory.prefetch([identity.userId]);
  }, [directory, identity]);

  // Prune expired typing pills every 2s. Simple wall-clock sweep —
  // typing state is ephemeral anyway, so we don't need precision.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingByConv((prev) => {
        let mutated = false;
        const next = new Map(prev);
        for (const [cid, inner] of prev) {
          const innerNext = new Map(inner);
          let changed = false;
          for (const [uid, expiresAt] of inner) {
            if (expiresAt <= now) {
              innerNext.delete(uid);
              changed = true;
            }
          }
          if (changed) {
            mutated = true;
            if (innerNext.size === 0) next.delete(cid);
            else next.set(cid, innerNext);
          }
        }
        return mutated ? next : prev;
      });
    }, 2_000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Bootstrap the conversation manager + initial server-side setup.
  //
  // Three phases, each independently resilient:
  //   1. Local hydrate (ConversationManager.create + hydrate messages).
  //      Failure here means IndexedDB is unhappy — unrecoverable.
  //   2. Device / KP publish to the server. Failure means the user
  //      can read their existing chats but can't be invited to new
  //      ones until the server recovers; we log it but keep going.
  //   3. Set state + enable poll.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const mgr = await ConversationManager.create({
          identity,
          client,
          store,
          tokenProvider: () => token,
        });
        try {
          await client.registerDevice(token, mgr.buildDeviceRegistration());
          await mgr.refillKeyPackages(3);
        } catch (err) {
          if (!cancelled) {
            const msg = err instanceof Error ? err.message : '';
            // Transient network failure during init — don't alarm
            // the user; the background poll retries, and the
            // hydrated state already loaded.
            if (!/failed to fetch|network|load failed/i.test(msg)) {
              setError(
                msg
                  ? `server sync degraded: ${msg} (conversations still readable)`
                  : 'server sync degraded',
              );
            }
          }
        }
        if (cancelled) return;
        setManager(mgr);
        setAttachments(
          new AttachmentService({
            client,
            tokenProvider: () => token,
            uploaderUserId: identity.userId,
          }),
        );
        setConversations(mgr.conversations());

        // Hydrate persisted messages + poll cursors so the conversation
        // history survives reloads. MLS has already ratcheted past these
        // messages on the server; we can't re-decrypt them from /history,
        // so the local plaintext store is the only way to preserve the
        // user-visible view.
        const hydrated = new Map<ConversationId, LocalMessage[]>();
        for (const cid of mgr.conversations()) {
          const stored = await mgr.loadMessages(cid);
          const mapped: LocalMessage[] = stored.map((m) => storedToLocal(m));
          if (mapped.length > 0) hydrated.set(cid, mapped);
          const lastSeq = await mgr.getLastSeq(cid);
          if (lastSeq > 0) sinceRef.current.set(cid, lastSeq);
        }
        if (cancelled) return;
        setMessages(hydrated);

        setReady(true);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'init failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity, client, store, token]);

  // Re-render whenever conversation metadata (name, members) changes.
  // The version counter bumps on every persistMeta() / removeConversation()
  // call so React picks up renames and member-list updates even when no
  // other React state changed.
  useSyncExternalStore(
    useCallback(
      (fn: () => void) => {
        if (!manager) return () => undefined;
        return manager.subscribeMeta(fn);
      },
      [manager],
    ),
    useCallback(() => manager?.metaVersion() ?? 0, [manager]),
    () => 0,
  );

  // Keep the conversation list in sync with the manager's current view
  // (covers removeConversation rollbacks which aren't covered by
  // acceptInvitations + newConversation's explicit setConversations).
  useEffect(() => {
    if (!manager) return;
    const unsub = manager.subscribeMeta(() => {
      setConversations(manager.conversations());
    });
    return () => {
      unsub();
    };
  }, [manager]);

  // Prefetch members for every conversation the user is part of so the
  // sidebar labels hydrate in a single batch instead of N requests.
  useEffect(() => {
    if (!manager) return;
    const ids = new Set<string>();
    for (const cid of conversations) {
      for (const m of manager.members(cid)) ids.add(m);
    }
    directory.prefetch([...ids]);
  }, [manager, conversations, directory]);

  const hydrateAttachment = useCallback(
    async (reference: AttachmentReference): Promise<string | undefined> => {
      if (!attachments) return undefined;
      try {
        const bytes = await attachments.download(reference);
        // Copy into a fresh ArrayBuffer for Blob's stricter typing across runtimes.
        const buffer = new Uint8Array(new ArrayBuffer(bytes.length));
        buffer.set(bytes);
        // Force application/octet-stream regardless of the sender's claimed
        // mime. A malicious group member otherwise could set mime: text/html
        // on an attachment, and clicking the resulting blob: URL would
        // navigate-and-execute in our origin. The UI layer still uses
        // reference.mime to decide between <img> and <a download>, but the
        // underlying Blob stays opaque bytes.
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        return URL.createObjectURL(blob);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'attachment download failed');
        return undefined;
      }
    },
    [attachments],
  );

  // Per-conversation inbox of envelopes waiting to be processed, kept
  // sorted by server-sequence. MLS requires strict in-order processing
  // of application messages (each sender's generation number must be
  // consumed in sequence; skipping to a higher one triggers the
  // "Desired gen in the past" decryption failure). The two sources we
  // ingest from — the /history poll and the per-conversation WebSocket
  // — can race: WS can deliver N+2 while poll is still fetching N and
  // N+1. The queue + drainer below backfill via /history whenever we
  // see a gap, then process strictly in order.
  interface PendingEnvelope {
    readonly sequence: number;
    readonly senderDevice: string;
    readonly receivedAt: number;
    readonly ciphertext: string;
    readonly senderUserId?: UserId;
  }
  interface QueueState {
    pending: PendingEnvelope[];
    draining: boolean;
  }
  const queuesRef = useRef<Map<ConversationId, QueueState>>(new Map());

  const processOneEnvelope = useCallback(
    async (id: ConversationId, entry: PendingEnvelope): Promise<void> => {
      if (!manager) return;
      sinceRef.current.set(id, entry.sequence);
      if (entry.senderDevice === identity.deviceId) return;
      try {
        const plaintext = await manager.receive(id, fromBase64Url(entry.ciphertext));
        // Handshake envelopes (commits / proposals) advance the MLS
        // state but produce no plaintext. `receive` already
        // persisted the new state + refreshed members, so there's
        // nothing more to show.
        if (plaintext === undefined) return;
        const payload = decodePayload(plaintext);
        // Drop blocked senders entirely — applies to every envelope
        // type (text, reaction, deletion, typing, readUpTo, game).
        // We still advance sinceRef (top of fn) so /history won't
        // keep handing us the same block.
        if (entry.senderUserId && manager.isBlocked(entry.senderUserId)) return;
        let objectUrl: string | undefined;
        if (payload.attachment) {
          objectUrl = await hydrateAttachment(payload.attachment);
        }
        if (payload.game?.senderUserId) {
          ingestGameMessage(id, payload.game, payload.game.senderUserId);
        }
        // Text / attachment messages don't carry a senderUserId in
        // the payload — only game moves do. The server attests who
        // authenticated the /send (or WS frame) on the envelope, so
        // that's the source of truth for avatars in N-way rooms.
        // Fall back to the sole peer for 1:1 and legacy history.
        const peers = manager.members(id).filter((m) => m !== identity.userId);
        const senderUserId =
          payload.game?.senderUserId ??
          entry.senderUserId ??
          (peers.length === 1 ? peers[0] : undefined);

        // Typing envelope: refresh the "X is typing" pill and
        // return. Nothing persisted, nothing appended.
        if (payload.typing && senderUserId && senderUserId !== identity.userId) {
          const expiresAt = Date.now() + TYPING_EXPIRY_MS;
          const typer = senderUserId;
          setTypingByConv((prev) => {
            const next = new Map(prev);
            const existing = next.get(id);
            const inner = new Map<UserId, number>(existing ?? []);
            inner.set(typer, expiresAt);
            next.set(id, inner);
            return next;
          });
          return;
        }

        // Read-receipt envelope: update per-peer read cursor so own
        // messages ≤ readUpTo can render a "seen" mark. No append.
        if (payload.readUpTo !== undefined && senderUserId && senderUserId !== identity.userId) {
          const reader = senderUserId;
          const upTo = payload.readUpTo;
          setReadReceipts((prev) => {
            const next = new Map(prev);
            const existing = next.get(id);
            const inner = new Map<UserId, number>(existing ?? []);
            const current = inner.get(reader) ?? 0;
            if (upTo > current) inner.set(reader, upTo);
            next.set(id, inner);
            return next;
          });
          return;
        }

        // Pin envelope: update the conversation's pinnedSequences
        // set. Any member can pin or unpin; no sender check beyond
        // being a real conversation member (which the DO already
        // enforces on /send).
        if (payload.pin) {
          await manager.applyPinUpdate(id, payload.pin.targetSequence, payload.pin.pinned);
          return;
        }

        // Group-avatar envelope: any member can set / clear the
        // shared group avatar. Pure metadata; no bubble.
        if (payload.groupAvatar) {
          await manager.applyGroupAvatarUpdate(
            id,
            payload.groupAvatar.avatarMediaId,
            payload.groupAvatar.uploaderUserId,
          );
          return;
        }

        // Group-rename envelope: any member can set / clear the
        // shared group name. Pure metadata.
        if (payload.groupName) {
          await manager.applyGroupNameUpdate(id, payload.groupName.name);
          return;
        }

        // Reaction envelope: mutate target message's reactions map.
        // Persist the advance via the sinceRef (already set at the
        // top of this fn) and the reactions write below. Don't
        // appendMessage — this envelope is pure metadata, not a
        // bubble.
        if (payload.reaction && senderUserId) {
          const updated = await manager.applyReaction(
            id,
            payload.reaction.targetSequence,
            senderUserId,
            payload.reaction.emoji,
          );
          if (updated) {
            setMessages((prev) => patchMessage(prev, id, updated.sequence, storedToLocal(updated)));
          }
          return;
        }

        // Deletion envelope: honour only when the sender matches the
        // original message's sender. Guards against a member trying
        // to delete someone else's content.
        if (payload.deletion && senderUserId) {
          const target = await manager.getStoredMessage(id, payload.deletion.targetSequence);
          if (target && target.senderUserId === senderUserId) {
            const updated = await manager.applyDeletion(id, payload.deletion.targetSequence);
            if (updated) {
              setMessages((prev) =>
                patchMessage(prev, id, updated.sequence, storedToLocal(updated)),
              );
            }
          }
          return;
        }

        // A real message from a peer means they've clearly stopped
        // typing; drop their pill even if the expiry hasn't hit yet.
        if (senderUserId && senderUserId !== identity.userId) {
          setTypingByConv((prev) => {
            const inner = prev.get(id);
            if (!inner || !inner.has(senderUserId)) return prev;
            const next = new Map(prev);
            const innerNext = new Map(inner);
            innerNext.delete(senderUserId);
            if (innerNext.size === 0) next.delete(id);
            else next.set(id, innerNext);
            return next;
          });
        }
        const incoming: LocalMessage = {
          direction: 'received',
          payload,
          at: entry.receivedAt,
          sequence: entry.sequence,
          ...(senderUserId ? { senderUserId } : {}),
          ...(objectUrl ? { attachmentObjectUrl: objectUrl } : {}),
        };
        setMessages((prev) => appendMessage(prev, id, incoming));
        await manager.saveMessage({
          conversationId: id,
          sequence: entry.sequence,
          direction: 'received',
          at: entry.receivedAt,
          ...(senderUserId ? { senderUserId } : {}),
          payload,
        });
        // If the user is currently viewing this conversation AND the
        // tab is visible, the message they're about to see (or just
        // saw) counts as read — advance the readUpTo cursor so the
        // sidebar doesn't light up.
        if (
          selectedRef.current === id &&
          typeof document !== 'undefined' &&
          document.visibilityState === 'visible'
        ) {
          await manager.markRead(id, entry.sequence);
          // Tell peers we've seen up to this sequence — but only for
          // user-facing content (text / attachment). Game moves and
          // other metadata shouldn't trigger per-envelope receipts;
          // otherwise an active tic-tac-toe match would double its
          // wire traffic.
          if (payload.text !== undefined || payload.attachment !== undefined) {
            sendReadReceipt(id, entry.sequence);
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'decrypt failed';
        // "epoch too old" is expected when the DO backfills history
        // that predates our join — the sender was on an epoch whose
        // keys we never had. Advancing sinceRef (above) already
        // silently skips it going forward; don't spook the user.
        if (msg.toLowerCase().includes('epoch too old')) return;
        setError(msg);
      }
    },
    [manager, identity.deviceId, hydrateAttachment],
  );

  const drainQueue = useCallback(
    async (id: ConversationId): Promise<void> => {
      const q = queuesRef.current.get(id);
      if (!q || q.draining) return;
      q.draining = true;
      try {
        while (q.pending.length > 0) {
          const head = q.pending[0]!;
          const last = sinceRef.current.get(id) ?? 0;
          if (head.sequence <= last) {
            q.pending.shift();
            continue;
          }
          if (head.sequence > last + 1) {
            // Gap — fetch /history since last to backfill. Results
            // come in sorted; merge any missing into the queue.
            try {
              const history = await client.fetchHistory(token, id, last);
              for (const h of history) {
                if (q.pending.some((e) => e.sequence === h.sequence)) continue;
                const backfilled: PendingEnvelope = {
                  sequence: h.sequence,
                  senderDevice: h.senderDevice,
                  receivedAt: h.receivedAt,
                  ciphertext: h.ciphertext,
                  ...(h.senderUserId ? { senderUserId: h.senderUserId as UserId } : {}),
                };
                const idx = q.pending.findIndex((e) => e.sequence > h.sequence);
                if (idx < 0) q.pending.push(backfilled);
                else q.pending.splice(idx, 0, backfilled);
              }
              // If the backfill returned nothing past `last`, the gap
              // can't actually be filled by the server (e.g. messages
              // already trimmed). Advance past the hole so we don't
              // spin forever.
              const stillGap =
                q.pending.length > 0 &&
                q.pending[0]!.sequence > (sinceRef.current.get(id) ?? 0) + 1;
              if (stillGap) {
                sinceRef.current.set(id, q.pending[0]!.sequence - 1);
              }
              continue;
            } catch (err) {
              // Same forgiving fallback the old poll had — transient
              // failures + 403s (conversation no longer reachable).
              const status = (err as { status?: number }).status;
              if (status === 403 || status === 404) {
                q.pending.length = 0;
              }
              return;
            }
          }
          // In-order: process the head.
          q.pending.shift();
          await processOneEnvelope(id, head);
        }
      } finally {
        q.draining = false;
      }
    },
    [client, token, processOneEnvelope],
  );

  const ingestEnvelope = useCallback(
    async (id: ConversationId, entry: PendingEnvelope): Promise<void> => {
      if (!manager) return;
      const last = sinceRef.current.get(id) ?? 0;
      if (entry.sequence <= last) return;
      let q = queuesRef.current.get(id);
      if (!q) {
        q = { pending: [], draining: false };
        queuesRef.current.set(id, q);
      }
      if (q.pending.some((e) => e.sequence === entry.sequence)) return;
      const idx = q.pending.findIndex((e) => e.sequence > entry.sequence);
      if (idx < 0) q.pending.push(entry);
      else q.pending.splice(idx, 0, entry);
      await drainQueue(id);
    },
    [manager, drainQueue],
  );

  const poll = useCallback(async () => {
    if (!manager) return;
    try {
      for (const id of manager.conversations()) {
        // Freshly-created conversations exist only locally until the
        // owner invites someone — the server's DO has no member list
        // yet and /history would return 403. Skip polling until we
        // know there's at least one peer, otherwise the 403-handler
        // below would mistakenly tear the conversation down and
        // bounce the user back to the lobby.
        const peers = manager.members(id).filter((m) => m !== identity.userId);
        if (peers.length === 0) continue;

        const since = sinceRef.current.get(id) ?? 0;
        let history;
        try {
          history = await client.fetchHistory(token, id, since);
        } catch (err) {
          // 403 from a conversation means the server's DO no longer lists
          // us as a member — typically a conversation that was created
          // under a previous identity (reset flow) or the owner removed
          // us. Drop it locally so the sidebar self-heals instead of
          // spamming errors every poll tick.
          const status = (err as { status?: number }).status;
          if (status === 403 || status === 404) {
            sinceRef.current.delete(id);
            setMessages((prev) => {
              const next = new Map(prev);
              next.delete(id);
              return next;
            });
            if (selectedRef.current === id) {
              setSelected(undefined);
            }
            await manager.removeConversation(id);
            continue;
          }
          throw err;
        }
        for (const entry of history) {
          await ingestEnvelope(id, {
            sequence: entry.sequence,
            senderDevice: entry.senderDevice,
            receivedAt: entry.receivedAt,
            ciphertext: entry.ciphertext,
            ...(entry.senderUserId ? { senderUserId: entry.senderUserId as UserId } : {}),
          });
        }
      }
    } catch (err) {
      // Swallow transient network blips — the next poll tick will
      // retry. A "Failed to fetch" banner flickering across the UI
      // every time the phone loses a packet is worse than silent
      // recovery. Real errors (403s from the inner block have
      // already been handled above) still surface.
      const msg = err instanceof Error ? err.message : String(err);
      if (/failed to fetch|network|load failed/i.test(msg)) {
        return;
      }
      setError(msg || 'poll failed');
    }
  }, [manager, client, token, ingestEnvelope, identity.userId]);

  // Message-history poll. Hits the DO /history endpoint per conversation;
  // cheap (DO storage, different quota from KV). Paused when the tab
  // is hidden so a backgrounded PWA doesn't chew through requests.
  // Acts as a backfill safety net for the per-conversation WebSocket
  // below — if the WS drops, the next poll tick catches up.
  useEffect(() => {
    if (!ready) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (timer) return;
      void poll();
      timer = setInterval(() => void poll(), pollIntervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    if (document.visibilityState === 'visible') start();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [ready, poll, pollIntervalMs]);

  // Per-conversation WebSocket for the currently-selected room.
  // The conversation DO already broadcasts a `{kind:"message", envelope}`
  // payload to every connected peer on /send, so once the socket is up
  // the client sees new messages instantly without waiting for the
  // poll tick. We open it lazily on selection so an idle user with N
  // conversations doesn't pay N concurrent WS upgrades + KV ledger
  // writes — only the conversation actually being viewed gets one.
  // Auto-reconnect with backoff on close; falls back transparently to
  // the history poll when the WS is down.
  // Mark the selected conversation read whenever selection changes
  // or visibility returns. Covers the "user switches back to a
  // room they already scrolled past" case — the unread badge
  // clears as they land.
  useEffect(() => {
    if (!manager || !selected) return;
    void manager.markRead(selected);
    // Emit a read receipt for the latest sequence we know about so
    // peers see their messages flip to "seen" when we open/refocus
    // the conversation.
    const latest = manager.meta(selected)?.latestSequence;
    if (latest) sendReadReceipt(selected, latest);
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void manager.markRead(selected);
        const l = manager.meta(selected)?.latestSequence;
        if (l) sendReadReceipt(selected, l);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [manager, selected]);

  // The DO rejects WS upgrades for non-members with 403. A
  // just-created conversation has no peers and no server-side
  // member list yet, so skip opening a socket until an invite
  // bootstraps membership. `selectedPeerCount` is a reactive dep
  // for the effect below — re-reading `manager.members(selected)`
  // whenever Chat re-renders means the effect will re-run once the
  // first invite adds a peer.
  const selectedPeerCount =
    selected && manager ? manager.members(selected).filter((m) => m !== identity.userId).length : 0;

  useEffect(() => {
    if (!ready || !manager || !selected) return;
    if (selectedPeerCount === 0) return;
    const target = selected;
    let cancelled = false;
    let ws: WebSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectDelay = 2_000;

    const connect = () => {
      if (cancelled) return;
      void (async () => {
        try {
          const socket = await client.openWebSocket(token, target);
          if (cancelled) {
            socket.close();
            return;
          }
          ws = socket;
          reconnectDelay = 2_000;
          socket.onmessage = (event) => {
            if (typeof event.data !== 'string') return;
            let parsed: unknown;
            try {
              parsed = JSON.parse(event.data);
            } catch {
              return;
            }
            const msg = parsed as {
              kind?: string;
              envelope?: {
                sequence?: number;
                senderDevice?: string;
                receivedAt?: number;
                ciphertext?: string;
                senderUserId?: string;
              };
            };
            const env = msg.envelope;
            if (
              msg.kind !== 'message' ||
              !env ||
              typeof env.sequence !== 'number' ||
              typeof env.senderDevice !== 'string' ||
              typeof env.receivedAt !== 'number' ||
              typeof env.ciphertext !== 'string'
            ) {
              return;
            }
            void ingestEnvelope(target, {
              sequence: env.sequence,
              senderDevice: env.senderDevice,
              receivedAt: env.receivedAt,
              ciphertext: env.ciphertext,
              ...(typeof env.senderUserId === 'string'
                ? { senderUserId: env.senderUserId as UserId }
                : {}),
            });
          };
          socket.onclose = () => {
            ws = undefined;
            if (cancelled) return;
            // Exponential-ish backoff up to 30s. The poll loop covers
            // delivery in the meantime.
            reconnectTimer = setTimeout(connect, reconnectDelay);
            reconnectDelay = Math.min(30_000, reconnectDelay * 2);
          };
          socket.onerror = () => {
            try {
              socket.close();
            } catch {
              /* already closed */
            }
          };
        } catch {
          if (cancelled) return;
          reconnectTimer = setTimeout(connect, reconnectDelay);
          reconnectDelay = Math.min(30_000, reconnectDelay * 2);
        }
      })();
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      if (ws) {
        try {
          ws.close();
        } catch {
          /* already closed */
        }
        ws = undefined;
      }
    };
    // selectedPeerCount is a dep so the effect re-runs once an
    // invite bootstraps server-side membership — the first
    // `manager.invite` call pushes a peer into local meta, the
    // parent re-renders with a non-zero count, and the WS opens.
  }, [ready, manager, selected, selectedPeerCount, client, token, ingestEnvelope]);

  // Invitation drain — two paths:
  //
  //   a) Server-push via WebSocket to /notifications/ws. When the
  //      socket is alive, the server sends a `{"type":"invitation"}`
  //      nudge every time a new invite lands; we react by draining.
  //      Zero polling required, battery-friendly, and works silently
  //      (no notification permission needed).
  //
  //   b) Exponential-backoff poll as a fallback. Kicks in when the WS
  //      can't be established, the connection drops, or the tab has
  //      been hidden long enough that the socket was closed. Base
  //      interval doubles on empty drains up to 120 s; a non-empty
  //      drain snaps back to the base.
  useEffect(() => {
    if (!ready || !manager) return;
    const baseInterval = invitationPollIntervalMs;
    const maxInterval = Math.max(baseInterval, 120_000);
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let currentInterval = baseInterval;
    let running = false;
    let ws: WebSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const drain = async () => {
      if (running) return;
      running = true;
      try {
        const joined = await manager.acceptInvitations();
        if (joined.length > 0) {
          setConversations(manager.conversations());
          currentInterval = baseInterval;
        } else {
          currentInterval = Math.min(maxInterval, currentInterval * 2);
        }
      } catch {
        /* not fatal — next tick will retry */
      } finally {
        running = false;
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      pollTimer = setTimeout(() => {
        void (async () => {
          await drain();
          scheduleNext();
        })();
      }, currentInterval);
    };

    const startPoll = () => {
      if (pollTimer || cancelled) return;
      void (async () => {
        await drain();
        scheduleNext();
      })();
    };
    const stopPoll = () => {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = undefined;
      }
    };

    const connectWs = () => {
      if (cancelled || ws) return;
      void (async () => {
        try {
          const socket = await client.openNotificationWebSocket(token);
          if (cancelled) {
            socket.close();
            return;
          }
          ws = socket;
          socket.onmessage = (event) => {
            if (typeof event.data !== 'string') return;
            try {
              const parsed = JSON.parse(event.data) as { type?: string };
              if (parsed.type === 'invitation') {
                currentInterval = baseInterval;
                void drain();
              }
            } catch {
              /* ignore malformed nudges */
            }
          };
          socket.onclose = () => {
            ws = undefined;
            if (cancelled) return;
            // Re-attempt in 5s; meanwhile the poll fallback keeps ticks.
            if (!reconnectTimer) {
              reconnectTimer = setTimeout(() => {
                reconnectTimer = undefined;
                connectWs();
              }, 5_000);
            }
          };
          socket.onerror = () => {
            try {
              socket.close();
            } catch {
              /* already closed */
            }
          };
        } catch {
          // Fall back to polling alone. Retry in 30s in case the
          // server was transiently unreachable.
          if (!reconnectTimer && !cancelled) {
            reconnectTimer = setTimeout(() => {
              reconnectTimer = undefined;
              connectWs();
            }, 30_000);
          }
        }
      })();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        startPoll();
        connectWs();
      } else {
        stopPoll();
      }
    };

    if (document.visibilityState === 'visible') {
      startPoll();
      connectWs();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      stopPoll();
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      if (ws) {
        try {
          ws.close();
        } catch {
          /* already closed */
        }
        ws = undefined;
      }
    };
  }, [ready, manager, invitationPollIntervalMs, client, token]);

  async function handleCreateConversation() {
    if (!manager) return;
    const id = await manager.newConversation();
    setConversations(manager.conversations());
    setSelected(id);
  }

  async function handleFindOpponent(engineId: string) {
    if (!manager) return;
    setMatchmakingStatus('searching');
    try {
      const result = await client.findMatch(token, engineId);
      if (result.status === 'waiting') {
        setMatchmakingStatus('waiting');
        return;
      }
      if (result.status === 'matched' && result.opponent) {
        const id = await manager.newConversation();
        setConversations(manager.conversations());
        setSelected(id);
        await manager.invite(id, result.opponent);
        directory.prefetch([result.opponent]);
        await sendGameMessage(id, {
          engineId,
          moveIndex: 0,
          move: null,
          players: [identity.userId, result.opponent],
          senderUserId: identity.userId,
        });
        setMatchmakingStatus('idle');
      }
    } catch (err) {
      setMatchmakingStatus('idle');
      setError(err instanceof Error ? err.message : 'matchmaking failed');
    }
  }

  async function recordGameResult(
    conversationId: ConversationId,
    engineId: string,
    terminal: { readonly winner?: UserId; readonly draw?: true },
  ): Promise<void> {
    if (!manager) return;
    try {
      const updated = await manager.recordGameResult(conversationId, engineId, {
        ...(terminal.winner ? { winner: terminal.winner } : {}),
        ...(terminal.draw ? { draw: true } : {}),
      });
      setGameScores((prev) => {
        const next = new Map(prev);
        next.set(`${conversationId}:${engineId}`, updated);
        return next;
      });
    } catch {
      // non-fatal: the board + terminal state render correctly even if
      // persistence hiccups. Next reload will just pick up whatever
      // score we managed to write earlier.
    }
  }

  // Lazily hydrate the score for the currently-active game so the
  // panel renders the running tally the first time a game appears.
  useEffect(() => {
    if (!manager || !selected) return;
    const session = gameSessions.get(selected);
    if (!session) return;
    const key = `${selected}:${session.engineId}`;
    if (gameScores.has(key)) return;
    void (async () => {
      const score = await manager.loadGameScore(selected, session.engineId);
      setGameScores((prev) => {
        if (prev.has(key)) return prev;
        const next = new Map(prev);
        next.set(key, score);
        return next;
      });
    })();
  }, [manager, selected, gameSessions, gameScores]);

  function ingestGameMessage(
    conversationId: ConversationId,
    game: GameMessage,
    sender: UserId,
  ): void {
    const engine = gameRegistry.get(game.engineId);
    if (!engine) return;
    const existing = gameSessionsRef.current.get(conversationId);
    // moveIndex === 0 is a session bootstrap. Accept it either as the
    // first game in this conversation OR as a rematch after a terminal
    // state — otherwise a stray bootstrap mid-game would reset an
    // in-progress board.
    const isBootstrap = game.moveIndex === 0;
    const canRestart = isBootstrap && (!existing || engine.terminal(existing.state) !== null);
    const next =
      existing && !canRestart
        ? applyMessage(engine, existing, game, sender)
        : startSession(engine, game, sender);
    if ('kind' in next) {
      // out-of-order is a benign race (e.g. two simultaneous "play
      // again" clicks, or a late-arriving move after a restart).
      // Log but don't alarm the user — the game state is still
      // correct, just one player's click was superseded.
      if (next.kind === 'out-of-order') {
        console.warn(`game ${game.engineId}: dropped out-of-order move`, next);
        return;
      }
      setError(`game ${game.engineId}: ${JSON.stringify(next)}`);
      return;
    }
    // Score: if this message closed out the previous round, credit
    // the winner (or bump the draw counter) before swapping in the
    // fresh session state.
    const prevTerminal = existing ? engine.terminal(existing.state) : null;
    const newTerminal = engine.terminal(next.state);
    if (!prevTerminal && newTerminal) {
      void recordGameResult(conversationId, game.engineId, newTerminal);
    }
    setGameSessions((prev) => new Map(prev).set(conversationId, next));
  }

  async function handleStartTicTacToe(opponent: UserId | undefined) {
    await handleStartGame('tic-tac-toe', opponent);
  }

  async function handleStartGame(engineId: string, opponent: UserId | undefined) {
    if (!manager || !selected || !opponent) return;
    const engine = gameRegistry.get(engineId);
    if (!engine) return;
    const players: readonly UserId[] = [identity.userId, opponent];
    await sendGameMessage(selected, {
      engineId: engine.id,
      moveIndex: 0,
      move: null,
      players,
      senderUserId: identity.userId,
    });
  }

  async function handlePlayAgain(conversationId: ConversationId) {
    if (!manager) return;
    const session = gameSessionsRef.current.get(conversationId);
    if (!session) return;
    const engine = gameRegistry.get(session.engineId);
    if (!engine || engine.terminal(session.state) === null) return;
    // Keep the exact player order from the previous round. Both
    // players' clients compute the same `players` array, so if both
    // click "play again" simultaneously the two moveIndex=0 bootstraps
    // are identical payloads — the first arrives, the second is
    // rejected as out-of-order against the fresh session, and both
    // sides converge on the same state. Previously we reversed the
    // order for nicer alternation, but that let concurrent clicks
    // produce two different `players` arrays and desync the boards.
    await sendGameMessage(conversationId, {
      engineId: session.engineId,
      moveIndex: 0,
      move: null,
      players: [...session.players],
      senderUserId: identity.userId,
    });
  }

  async function handleGameMove(conversationId: ConversationId, engineId: string, move: unknown) {
    const engine = gameRegistry.get(engineId);
    if (!engine) return;
    const existing = gameSessionsRef.current.get(conversationId);
    if (!existing) return;
    const validation = engine.validateMove(existing.state, move, identity.userId);
    if (validation) {
      setError(validation);
      return;
    }
    await sendGameMessage(conversationId, {
      engineId,
      moveIndex: existing.nextMoveIndex,
      move,
      senderUserId: identity.userId,
    });
  }

  async function sendGameMessage(conversationId: ConversationId, game: GameMessage) {
    if (!manager) return;
    const withSender: GameMessage = { ...game, senderUserId: game.senderUserId ?? identity.userId };
    const payload: ChatPayload = { game: withSender };
    try {
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const gameRecipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      const sequence = await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        gameRecipients,
        { silent: true },
      );
      ingestGameMessage(conversationId, withSender, identity.userId);
      const sentAt = Date.now();
      setMessages((prev) =>
        appendMessage(prev, conversationId, {
          direction: 'sent',
          payload,
          at: sentAt,
          sequence,
          senderUserId: identity.userId,
        }),
      );
      await manager.saveMessage({
        conversationId,
        sequence,
        direction: 'sent',
        at: sentAt,
        senderUserId: identity.userId,
        payload,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'game move failed');
    }
  }

  /**
   * Fire-and-forget for pure metadata envelopes (typing, readUpTo,
   * etc.). Doesn't touch local message state, doesn't save a
   * StoredMessage — the whole point is that these are ephemeral or
   * aggregate-only. Errors swallowed so a network blip doesn't
   * surface a red banner over something the user didn't explicitly
   * do.
   */
  async function sendEphemeral(
    conversationId: ConversationId,
    payload: ChatPayload,
  ): Promise<void> {
    if (!manager) return;
    try {
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const recipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
          // V5-MEDIUM-03: typing + readUpTo never need to be replayed
          // from /history — broadcast-only. The DO honours this by
          // skipping ctx.storage.put for the envelope.
          ephemeral: true,
        },
        recipients,
      );
    } catch {
      // swallow — next keystroke / scroll will retry
    }
  }

  function maybeSendTyping(conversationId: ConversationId): void {
    if (!manager) return;
    const now = Date.now();
    const last = lastTypingEmitRef.current.get(conversationId) ?? 0;
    if (now - last < TYPING_EMIT_THROTTLE_MS) return;
    lastTypingEmitRef.current.set(conversationId, now);
    void sendEphemeral(conversationId, { typing: true });
  }

  function sendReadReceipt(conversationId: ConversationId, sequence: number): void {
    if (!manager) return;
    if (sequence <= 0) return;
    void sendEphemeral(conversationId, { readUpTo: sequence });
  }

  /**
   * Toggle a reaction on a received message from the current user.
   * Produces a reaction envelope that other members fold into their
   * view of the target's reactions map. Locally we mutate the target
   * via manager.applyReaction and patch the in-memory state.
   */
  async function toggleReaction(
    conversationId: ConversationId,
    targetSequence: number,
    emoji: string,
  ): Promise<void> {
    if (!manager) return;
    try {
      const payload: ChatPayload = { reaction: { targetSequence, emoji } };
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const recipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
        { silent: true },
      );
      const updated = await manager.applyReaction(
        conversationId,
        targetSequence,
        identity.userId,
        emoji,
      );
      if (updated) {
        setMessages((prev) =>
          patchMessage(prev, conversationId, updated.sequence, storedToLocal(updated)),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'reaction failed');
    }
  }

  /**
   * Delete-for-everyone on a specific message. Only the original
   * sender may invoke this; the receive side re-checks the sender
   * identity before honouring the envelope.
   */
  async function deleteMessage(
    conversationId: ConversationId,
    targetSequence: number,
  ): Promise<void> {
    if (!manager) return;
    try {
      const payload: ChatPayload = { deletion: { targetSequence } };
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const recipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
        { silent: true },
      );
      const updated = await manager.applyDeletion(conversationId, targetSequence);
      if (updated) {
        setMessages((prev) =>
          patchMessage(prev, conversationId, updated.sequence, storedToLocal(updated)),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed');
    }
  }

  // Tick the recording duration display while we're capturing audio.
  useEffect(() => {
    if (recordingState !== 'recording') return;
    const start = Date.now();
    setRecordingDuration(0);
    const interval = setInterval(() => {
      setRecordingDuration(Date.now() - start);
    }, 200);
    return () => {
      clearInterval(interval);
    };
  }, [recordingState]);

  async function startVoiceRecording(): Promise<void> {
    if (!manager || !selected || recordingState !== 'idle') return;
    const recorder = new VoiceRecorder();
    voiceRecorderRef.current = recorder;
    try {
      await recorder.start();
      setRecordingMode('voice');
      setRecordingState('recording');
    } catch (err) {
      voiceRecorderRef.current = undefined;
      setError(err instanceof Error ? err.message : 'mic access failed');
    }
  }

  /**
   * Broadcast a group-rename so every member's local meta picks
   * it up. Empty / null clears the override and reverts to the
   * member-derived label. No network round-trip is wasted on a
   * solo conversation (no peers to inform).
   */
  async function setGroupName(conversationId: ConversationId, name: string | null): Promise<void> {
    if (!manager) return;
    const trimmed = typeof name === 'string' ? name.trim() : null;
    const normalised = trimmed === null || trimmed === '' ? null : trimmed.slice(0, 80);
    try {
      await manager.applyGroupNameUpdate(conversationId, normalised);
      const peers = manager.members(conversationId).filter((m) => m !== identity.userId);
      if (peers.length === 0) return;
      const payload: ChatPayload = { groupName: { name: normalised } };
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        peers,
        { silent: true },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'rename failed');
    }
  }

  /**
   * Upload a new group avatar and broadcast it. The bytes go through
   * the same magic-byte-checked /users/avatar route as personal
   * avatars; the resulting mediaId is then propagated as a
   * groupAvatar payload so every member's local meta updates.
   */
  async function setGroupAvatar(conversationId: ConversationId, file: File): Promise<void> {
    if (!manager) return;
    if (!file.type.startsWith('image/')) {
      setError('avatar must be an image');
      return;
    }
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const { mediaId } = await client.uploadAvatar(token, bytes);
      const payload: ChatPayload = {
        groupAvatar: { avatarMediaId: mediaId, uploaderUserId: identity.userId },
      };
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const recipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
        { silent: true },
      );
      await manager.applyGroupAvatarUpdate(conversationId, mediaId, identity.userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'group avatar upload failed');
    }
  }

  /**
   * Toggle the pinned state of `targetSequence` in the given
   * conversation. Broadcasts a pin envelope so every member's
   * meta picks it up; locally we apply via manager.applyPinUpdate.
   */
  async function togglePin(
    conversationId: ConversationId,
    targetSequence: number,
    pinned: boolean,
  ): Promise<void> {
    if (!manager) return;
    try {
      const payload: ChatPayload = { pin: { targetSequence, pinned } };
      const ciphertext = await manager.send(conversationId, encodePayload(payload));
      const recipients = manager.members(conversationId).filter((m) => m !== identity.userId);
      await client.sendMessage(
        token,
        conversationId,
        {
          conversationId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
        { silent: true },
      );
      await manager.applyPinUpdate(conversationId, targetSequence, pinned);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'pin failed');
    }
  }

  /**
   * Forward an already-sent / received message into another
   * conversation. Reuses the same AttachmentReference for media —
   * the symmetric key inside the reference is portable, so members
   * of the destination conversation can decrypt the same blob with
   * a regular GET /media/. No re-upload, no re-encryption of the
   * payload (other than the fresh MLS frame for the new group).
   */
  async function forwardMessageTo(
    targetConvId: ConversationId,
    source: LocalMessage,
  ): Promise<void> {
    if (!manager) return;
    // Carry forward the original sender's attribution so receivers
    // can render "Forwarded from <name>" instead of silently treating
    // it as my own content (V5-LOW-06). Skip if the source has no
    // sender info — pre-V5 history entries — to avoid attaching
    // bogus "forwarded" badges. If the source itself was a forward,
    // we propagate the existing chain so a forward-of-a-forward
    // still points at the original author rather than the previous
    // forwarder.
    const inheritedAttribution =
      source.payload.forwardedFrom ??
      (source.senderUserId
        ? { senderUserId: source.senderUserId, originalAt: source.at }
        : undefined);
    const payload: ChatPayload = {
      ...(source.payload.text ? { text: source.payload.text } : {}),
      ...(source.payload.attachment ? { attachment: source.payload.attachment } : {}),
      ...(inheritedAttribution ? { forwardedFrom: inheritedAttribution } : {}),
    };
    if (!payload.text && !payload.attachment) return;
    try {
      const ciphertext = await manager.send(targetConvId, encodePayload(payload));
      const recipients = manager.members(targetConvId).filter((m) => m !== identity.userId);
      const sequence = await client.sendMessage(
        token,
        targetConvId,
        {
          conversationId: targetConvId,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
      );
      const sentAt = Date.now();
      setMessages((prev) =>
        appendMessage(prev, targetConvId, {
          direction: 'sent',
          payload,
          at: sentAt,
          sequence,
          senderUserId: identity.userId,
        }),
      );
      await manager.saveMessage({
        conversationId: targetConvId,
        sequence,
        direction: 'sent',
        at: sentAt,
        senderUserId: identity.userId,
        payload,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'forward failed');
    }
  }

  /**
   * Shared encrypt-upload-send path for recorded audio / video blobs.
   * Mirrors the file-attachment branch of handleSend but doesn't go
   * through the pendingFile state — recordings flow straight from
   * the recorder's stop callback.
   */
  async function sendRecordedBlob(
    bytes: Uint8Array,
    mime: string,
    filename: string,
  ): Promise<void> {
    if (!manager || !selected || !attachments) return;
    const attachment = await attachments.upload({ bytes, mime, filename });
    const ciphertext = await manager.send(selected, encodePayload({ attachment }));
    const recipients = manager.members(selected).filter((m) => m !== identity.userId);
    const sequence = await client.sendMessage(
      token,
      selected,
      {
        conversationId: selected,
        senderDevice: identity.deviceId,
        sentAt: Date.now(),
        ciphertext: toBase64Url(ciphertext),
      },
      recipients,
    );
    const objectUrl = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: mime }));
    const sentAt = Date.now();
    setMessages((prev) =>
      appendMessage(prev, selected, {
        direction: 'sent',
        payload: { attachment },
        at: sentAt,
        sequence,
        senderUserId: identity.userId,
        attachmentObjectUrl: objectUrl,
      }),
    );
    setForceScrollToken((n) => n + 1);
    await manager.saveMessage({
      conversationId: selected,
      sequence,
      direction: 'sent',
      at: sentAt,
      senderUserId: identity.userId,
      payload: { attachment },
    });
  }

  async function stopAndSendVoice(): Promise<void> {
    const recorder = voiceRecorderRef.current;
    if (!recorder) return;
    setRecordingState('uploading');
    try {
      const recording = await recorder.stop();
      voiceRecorderRef.current = undefined;
      const seconds = Math.max(1, Math.round(recording.durationMs / 1000));
      const ext = recording.mime.includes('webm')
        ? 'webm'
        : recording.mime.includes('ogg')
          ? 'ogg'
          : recording.mime.includes('mp4')
            ? 'm4a'
            : 'audio';
      await sendRecordedBlob(recording.bytes, recording.mime, `voice-${String(seconds)}s.${ext}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'voice send failed');
    } finally {
      setRecordingState('idle');
    }
  }

  function cancelVoiceRecording(): void {
    voiceRecorderRef.current?.cancel();
    voiceRecorderRef.current = undefined;
    setRecordingState('idle');
  }

  async function startVideoRecording(): Promise<void> {
    if (!manager || !selected || recordingState !== 'idle') return;
    const recorder = new VideoRecorder();
    videoRecorderRef.current = recorder;
    try {
      await recorder.start();
      setRecordingMode('video');
      setRecordingState('recording');
      // Bind the live preview after React commits the recording UI.
      // The video element is mounted only when recordingState !== idle
      // && recordingMode === 'video'; defer one tick so the ref is set.
      setTimeout(() => {
        const el = videoPreviewRef.current;
        const stream = recorder.getStream();
        if (el && stream) {
          el.srcObject = stream;
          el.play().catch(() => undefined);
        }
      }, 0);
    } catch (err) {
      videoRecorderRef.current = undefined;
      setError(err instanceof Error ? err.message : 'camera access failed');
    }
  }

  async function stopAndSendVideo(): Promise<void> {
    const recorder = videoRecorderRef.current;
    if (!recorder) return;
    setRecordingState('uploading');
    try {
      const recording = await recorder.stop();
      videoRecorderRef.current = undefined;
      const seconds = Math.max(1, Math.round(recording.durationMs / 1000));
      const ext = recording.mime.includes('webm') ? 'webm' : 'mp4';
      await sendRecordedBlob(recording.bytes, recording.mime, `video-${String(seconds)}s.${ext}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'video send failed');
    } finally {
      setRecordingState('idle');
    }
  }

  function cancelVideoRecording(): void {
    videoRecorderRef.current?.cancel();
    videoRecorderRef.current = undefined;
    setRecordingState('idle');
  }

  async function handleEnableNotifications() {
    const service = new PushService({
      client,
      token,
      userId: identity.userId,
      deviceId: identity.deviceId,
    });
    const outcome = await service.subscribe();
    setPushStatus(outcome.status);
    if (outcome.detail) setError(outcome.detail);
  }

  async function handleSend() {
    if (!manager || !selected) return;
    if (!draft && !pendingFile) return;
    try {
      let attachment: AttachmentReference | undefined;
      let outgoingFile: File | undefined;
      if (pendingFile && attachments) {
        // Phone-camera images frequently exceed the server's 10 MB
        // cap; downscale lossy + JPEG-encode them transparently so a
        // user picking a 12 MP shot from the gallery doesn't have to
        // think about it. Non-image files go through unchanged.
        const downscaled = await maybeDownscaleImage(pendingFile);
        outgoingFile = downscaled.file;
        const bytes = new Uint8Array(await outgoingFile.arrayBuffer());
        attachment = await attachments.upload({
          bytes,
          mime: outgoingFile.type || 'application/octet-stream',
          filename: outgoingFile.name,
        });
      }
      const payload: ChatPayload = {
        ...(draft ? { text: draft } : {}),
        ...(attachment ? { attachment } : {}),
        ...(replyToSequence !== undefined ? { replyToSequence } : {}),
      };
      const ciphertext = await manager.send(selected, encodePayload(payload));
      const recipients = manager.members(selected).filter((m) => m !== identity.userId);
      const sequence = await client.sendMessage(
        token,
        selected,
        {
          conversationId: selected,
          senderDevice: identity.deviceId,
          sentAt: Date.now(),
          ciphertext: toBase64Url(ciphertext),
        },
        recipients,
      );
      // Use the (possibly downscaled) outgoing file for the local
      // preview so the sender sees the same bytes peers will see.
      const objectUrl = outgoingFile ? URL.createObjectURL(outgoingFile) : undefined;
      const sentAt = Date.now();
      const message: LocalMessage = {
        direction: 'sent',
        payload,
        at: sentAt,
        sequence,
        senderUserId: identity.userId,
        ...(objectUrl ? { attachmentObjectUrl: objectUrl } : {}),
      };
      setMessages((prev) => appendMessage(prev, selected, message));
      setReplyToSequence(undefined);
      // Sending implicitly clears "I'm typing" on this side; reset
      // the throttle so the next keystroke emits fresh.
      lastTypingEmitRef.current.delete(selected);
      // Force the scroll view to snap to the bottom — if the user
      // had been reading older history, hitting send means they
      // want to see what they just posted.
      setForceScrollToken((n) => n + 1);
      await manager.saveMessage({
        conversationId: selected,
        sequence,
        direction: 'sent',
        at: sentAt,
        senderUserId: identity.userId,
        payload,
      });
      setDraft('');
      // Wipe the persisted draft now too — we just sent it.
      void manager.saveDraft(selected, '');
      setPendingFile(undefined);
    } catch (err) {
      setError(
        err instanceof Error
          ? `${err.name}: ${err.message || '(no message)'}`
          : `send failed: ${String(err)}`,
      );
    }
  }

  function handleComposerKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  const displayedMessages = useMemo(
    () => (selected ? (messages.get(selected) ?? []) : []),
    [selected, messages],
  );

  const selectedMeta = selected && manager ? manager.meta(selected) : undefined;
  const selectedMembers = selected && manager ? manager.members(selected) : [];

  return (
    <div className="app-body" data-testid="chat" data-has-selection={selected ? 'true' : 'false'}>
      <Sidebar
        client={client}
        token={token}
        directory={directory}
        identity={identity}
        conversations={conversations}
        selected={selected}
        onSelect={setSelected}
        onCreate={() => void handleCreateConversation()}
        onFindOpponent={() => void handleFindOpponent('tic-tac-toe')}
        onEnableNotifications={() => void handleEnableNotifications()}
        matchmakingStatus={matchmakingStatus}
        pushStatus={pushStatus}
        manager={manager}
        onDeleteConversation={(id) => {
          void (async () => {
            if (!manager) return;
            await manager.removeConversation(id);
            if (selectedRef.current === id) {
              setSelected(undefined);
            }
            setConversations(manager.conversations());
          })();
        }}
        {...(onExportBackup ? { onExportBackup } : {})}
        onSelectMessage={(id, sequence) => {
          setSelected(id);
          setScrollTo({ sequence, token: Date.now() });
        }}
      />
      <main className="main-panel">
        {error && (
          <div className="error-banner" data-testid="chat-error">
            {error}
            <button
              className="icon"
              aria-label="dismiss"
              style={{ float: 'right' }}
              onClick={() => {
                setError(undefined);
              }}
            >
              ✕
            </button>
          </div>
        )}
        <VideoCallPanel client={client} tokenProvider={() => token} />

        {selected && !manager && (
          <div style={{ padding: '2em', textAlign: 'center', color: 'var(--text-dim)' }}>
            loading conversation…
          </div>
        )}

        {!selected && (
          <section className="empty-lobby">
            <h2>Welcome to the lobby</h2>
            <p>
              Pick a conversation on the left, start a new one, or jump straight into a match with a
              random opponent.
            </p>
            <div className="game-cards">
              <button
                className="game-card"
                type="button"
                data-testid="lobby-find-opponent"
                onClick={() => void handleFindOpponent('tic-tac-toe')}
                disabled={matchmakingStatus !== 'idle'}
              >
                <span className="icon">⭕</span>
                <span className="name">Tic-tac-toe</span>
                <span className="desc">
                  {matchmakingStatus === 'searching'
                    ? 'Searching…'
                    : matchmakingStatus === 'waiting'
                      ? 'Waiting for an opponent…'
                      : 'Quick-match vs. a random player'}
                </span>
              </button>
              <button
                className="game-card"
                type="button"
                onClick={() => void handleCreateConversation()}
              >
                <span className="icon">💬</span>
                <span className="name">Blank chat</span>
                <span className="desc">
                  Start a conversation and invite someone by username or user id.
                </span>
              </button>
            </div>
          </section>
        )}

        {selected && manager && (
          <section className="conversation-view" data-testid="selected-conversation">
            <ConversationHeader
              conversationId={selected}
              {...(selectedMeta?.name ? { name: selectedMeta.name } : {})}
              members={selectedMembers}
              self={identity.userId}
              directory={directory}
              muted={selectedMeta?.muted === true}
              onRename={(name) => void setGroupName(selected, name ?? null)}
              onToggleMute={() => {
                void manager.setMuted(selected, !selectedMeta?.muted);
              }}
              onBlockMember={(userId) => {
                void (async () => {
                  try {
                    await manager.block(userId);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'block failed');
                  }
                })();
              }}
              onKickMember={(userId) => {
                void (async () => {
                  try {
                    await manager.kick(selected, userId);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'kick failed');
                  }
                })();
              }}
              {...(() => {
                const ga = selectedMeta?.groupAvatar;
                if (!ga) return {};
                const url = directory.avatarUrlFor(ga.uploaderUserId, ga.avatarMediaId);
                return url ? { groupAvatarUrl: url } : {};
              })()}
              onChangeAvatar={(file) => {
                void setGroupAvatar(selected, file);
              }}
              onExit={() => {
                setSelected(undefined);
              }}
              {...(gameRegistry.get('tic-tac-toe') &&
              !gameSessions.has(selected) &&
              selectedPeerCount > 0
                ? {
                    onStartTicTacToe: () => {
                      const opp = selectedMembers.find((m) => m !== identity.userId);
                      if (opp) void handleStartTicTacToe(opp);
                    },
                  }
                : {})}
              {...(gameRegistry.get('connect-four') &&
              !gameSessions.has(selected) &&
              selectedPeerCount > 0
                ? {
                    onStartConnectFour: () => {
                      const opp = selectedMembers.find((m) => m !== identity.userId);
                      if (opp) void handleStartGame('connect-four', opp);
                    },
                  }
                : {})}
              {...(gameRegistry.get('chess') && !gameSessions.has(selected) && selectedPeerCount > 0
                ? {
                    onStartChess: () => {
                      const opp = selectedMembers.find((m) => m !== identity.userId);
                      if (opp) void handleStartGame('chess', opp);
                    },
                  }
                : {})}
              onLeave={() => {
                void (async () => {
                  await manager.removeConversation(selected);
                  setSelected(undefined);
                  setConversations(manager.conversations());
                })();
              }}
              onDeleteForEveryone={() => {
                void (async () => {
                  try {
                    await manager.deleteConversationForEveryone(selected);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'delete failed');
                    return;
                  }
                  setSelected(undefined);
                  setConversations(manager.conversations());
                })();
              }}
              onExport={() => {
                void (async () => {
                  try {
                    const stored = await manager.loadMessages(selected);
                    const meta = manager.meta(selected);
                    const members = manager.members(selected);
                    const payload = {
                      version: 1,
                      exportedAt: new Date().toISOString(),
                      conversationId: selected,
                      name:
                        meta?.name ??
                        members
                          .filter((m) => m !== identity.userId)
                          .map((m) => directory.label(m))
                          .join(', ') ??
                        'chat',
                      members,
                      messages: stored,
                    };
                    const blob = new Blob([JSON.stringify(payload, null, 2)], {
                      type: 'application/json',
                    });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const slug =
                      (payload.name || 'chat')
                        .replace(/[^a-z0-9]+/gi, '-')
                        .replace(/^-|-$/g, '')
                        .toLowerCase()
                        .slice(0, 40) || 'chat';
                    const stamp = new Date().toISOString().slice(0, 10);
                    a.download = `gamez-${slug}-${stamp}.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'export failed');
                  }
                })();
              }}
            />
            <InviteBar
              client={client}
              token={token}
              self={identity.userId}
              existingMembers={selectedMembers}
              inviteTarget={inviteTarget}
              onInviteTargetChange={setInviteTarget}
              onInvite={(userId) => {
                setInviteTarget(userId);
                void (async () => {
                  if (!manager || !selected) return;
                  try {
                    await manager.invite(selected, userId as UserId);
                    directory.prefetch([userId]);
                    setInviteTarget('');
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    setError(
                      msg.includes('no key package available')
                        ? "that user isn't reachable right now — ask them to open the app so their device can publish new keys"
                        : msg,
                    );
                  }
                })();
              }}
            />
            {gameSessions.has(selected) &&
              (() => {
                const s = gameSessions.get(selected);
                const score = s ? gameScores.get(`${selected}:${s.engineId}`) : undefined;
                return (
                  <ActiveGame
                    conversationId={selected}
                    session={s}
                    self={identity.userId}
                    inviteTarget={inviteTarget as UserId}
                    directory={directory}
                    {...(score ? { score } : {})}
                    onStartTicTacToe={(opp) => void handleStartTicTacToe(opp)}
                    onMove={(eid, move) => void handleGameMove(selected, eid, move)}
                    onPlayAgain={() => void handlePlayAgain(selected)}
                  />
                );
              })()}
            {(selectedMeta?.pinnedSequences?.length ?? 0) > 0 && (
              <PinnedBar
                conversationId={selected}
                pinnedSequences={selectedMeta?.pinnedSequences ?? []}
                messages={displayedMessages}
                onJump={(sequence) => {
                  setScrollTo({ sequence, token: Date.now() });
                }}
                onUnpin={(sequence) => {
                  void togglePin(selected, sequence, false);
                }}
              />
            )}
            <MessageScroll
              // Force a fresh mount when the selected conversation
              // changes so sticky-bottom state resets and the new
              // conversation opens at the latest message regardless
              // of where scroll was in the previous one.
              key={selected}
              messages={displayedMessages}
              self={identity.userId}
              directory={directory}
              hydrateAttachment={hydrateAttachment}
              forceScrollToken={forceScrollToken}
              onReact={(targetSequence, emoji) => {
                void toggleReaction(selected, targetSequence, emoji);
              }}
              onReply={(targetSequence) => {
                setReplyToSequence(targetSequence);
              }}
              onDelete={(targetSequence) => {
                if (window.confirm('Delete this message for everyone? This cannot be undone.')) {
                  void deleteMessage(selected, targetSequence);
                }
              }}
              onForward={(message) => {
                setForwardingMessage(message);
              }}
              onOpenLightbox={(src, filename) => {
                setLightbox({ src, filename });
              }}
              onTogglePin={(targetSequence, pinned) => {
                void togglePin(selected, targetSequence, pinned);
              }}
              {...(selectedMeta?.pinnedSequences
                ? { pinnedSequences: new Set(selectedMeta.pinnedSequences) }
                : {})}
              {...(readReceipts.get(selected) ? { readReceipts: readReceipts.get(selected)! } : {})}
              {...(scrollTo ? { scrollTo } : {})}
            />
            {(() => {
              const typing = typingByConv.get(selected);
              if (!typing || typing.size === 0) return null;
              const now = Date.now();
              const users = Array.from(typing.entries())
                .filter(([, expiresAt]) => expiresAt > now)
                .map(([uid]) => directory.label(uid));
              if (users.length === 0) return null;
              const label =
                users.length === 1
                  ? `${users[0] ?? ''} is typing…`
                  : `${users.slice(0, -1).join(', ')} and ${users[users.length - 1] ?? ''} are typing…`;
              return (
                <div className="typing-pill" data-testid="typing-pill">
                  {label}
                </div>
              );
            })()}
            <div className="composer">
              {replyToSequence !== undefined &&
                (() => {
                  const target = displayedMessages.find((m) => m.sequence === replyToSequence);
                  if (!target) return null;
                  const label =
                    target.direction === 'sent'
                      ? directory.label(identity.userId)
                      : target.senderUserId
                        ? directory.label(target.senderUserId)
                        : '?';
                  const preview = replyPreviewText(target);
                  return (
                    <div className="reply-bar" data-testid="reply-bar">
                      <span className="reply-to">
                        <strong>{label}:</strong> <span className="truncate">{preview}</span>
                      </span>
                      <button
                        className="icon"
                        type="button"
                        aria-label="cancel reply"
                        title="Cancel reply"
                        onClick={() => {
                          setReplyToSequence(undefined);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })()}
              {pendingFile && (
                <div className="attached" data-testid="pending-file">
                  📎 {pendingFile.name}
                  <button
                    className="icon"
                    onClick={() => {
                      setPendingFile(undefined);
                    }}
                    aria-label="remove attachment"
                    type="button"
                  >
                    ✕
                  </button>
                </div>
              )}
              {recordingState !== 'idle' ? (
                <div
                  className={`voice-record-bar voice-record-${recordingMode}`}
                  data-testid="voice-record-bar"
                >
                  {recordingMode === 'video' && (
                    <video
                      ref={videoPreviewRef}
                      className="video-preview"
                      data-testid="video-preview"
                      muted
                      playsInline
                    />
                  )}
                  <div className="voice-record-meta">
                    <span className="voice-dot" aria-hidden="true">
                      ●
                    </span>
                    <span className="voice-duration">
                      {recordingState === 'uploading'
                        ? 'sending…'
                        : `${String(Math.floor(recordingDuration / 60_000))}:${String(
                            Math.floor((recordingDuration % 60_000) / 1000),
                          ).padStart(2, '0')}`}
                    </span>
                    <span className="grow" />
                    {recordingState === 'recording' && (
                      <>
                        <button
                          type="button"
                          className="ghost"
                          data-testid="record-cancel"
                          onClick={
                            recordingMode === 'video' ? cancelVideoRecording : cancelVoiceRecording
                          }
                        >
                          cancel
                        </button>
                        <button
                          type="button"
                          className="primary"
                          data-testid="record-stop"
                          onClick={() => {
                            if (recordingMode === 'video') {
                              void stopAndSendVideo();
                            } else {
                              void stopAndSendVoice();
                            }
                          }}
                        >
                          send
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <form
                  className="composer-row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSend();
                  }}
                >
                  <label className="attach-btn icon" aria-label="attach a file" title="attach">
                    📎
                    <input
                      data-testid="attach-input"
                      type="file"
                      onChange={(e) => {
                        setPendingFile(e.target.files?.[0]);
                      }}
                    />
                  </label>
                  {isVoiceRecordingAvailable() && (
                    <button
                      type="button"
                      className="icon"
                      data-testid="voice-record"
                      aria-label="record a voice message"
                      title="Record voice message"
                      onClick={() => void startVoiceRecording()}
                    >
                      🎤
                    </button>
                  )}
                  {isVideoRecordingAvailable() && (
                    <button
                      type="button"
                      className="icon"
                      data-testid="video-record"
                      aria-label="record a video message"
                      title="Record video message"
                      onClick={() => void startVideoRecording()}
                    >
                      📹
                    </button>
                  )}
                  <textarea
                    data-testid="message-input"
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      // Debounced typing indicator: only while the
                      // draft is non-empty (no one types with the
                      // field cleared) and throttled to one emit per
                      // TYPING_EMIT_THROTTLE_MS so active typists
                      // don't flood the wire.
                      if (selected && e.target.value.length > 0) {
                        maybeSendTyping(selected);
                      }
                    }}
                    onKeyDown={handleComposerKey}
                    placeholder="Type a message — Enter to send, Shift+Enter for newline"
                    rows={2}
                  />
                  <button
                    data-testid="send-submit"
                    type="submit"
                    className="send-btn primary"
                    disabled={!draft && !pendingFile}
                  >
                    send
                  </button>
                </form>
              )}
            </div>
          </section>
        )}
      </main>
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          filename={lightbox.filename}
          onClose={() => {
            setLightbox(undefined);
          }}
        />
      )}
      {forwardingMessage && (
        <ForwardPicker
          source={forwardingMessage}
          conversations={conversations}
          {...(selected ? { excludeConversation: selected } : {})}
          manager={manager}
          self={identity.userId}
          directory={directory}
          onCancel={() => {
            setForwardingMessage(undefined);
          }}
          onPick={(targetId) => {
            const source = forwardingMessage;
            setForwardingMessage(undefined);
            void forwardMessageTo(targetId, source);
          }}
        />
      )}
    </div>
  );
}

interface ConversationHeaderProps {
  readonly conversationId: ConversationId;
  readonly name?: string;
  readonly members: readonly UserId[];
  readonly self: UserId;
  readonly directory: MemberDirectory;
  readonly muted: boolean;
  readonly onRename: (name: string | undefined) => void;
  readonly onToggleMute: () => void;
  readonly onBlockMember: (userId: UserId) => void;
  readonly onKickMember: (userId: UserId) => void;
  readonly groupAvatarUrl?: string;
  readonly onChangeAvatar: (file: File) => void;
  /** Drop the conversation locally — keys + history gone from this device only. */
  readonly onLeave: () => void;
  /** Wipe the conversation server-side for every member. */
  readonly onDeleteForEveryone: () => void;
  readonly onExit: () => void;
  readonly onExport: () => void;
  /** When defined, a tic-tac-toe launcher icon appears next to delete. */
  readonly onStartTicTacToe?: () => void;
  /** When defined, a connect-four launcher icon appears next to tic-tac-toe. */
  readonly onStartConnectFour?: () => void;
  /** When defined, a chess launcher icon appears next to connect-four. */
  readonly onStartChess?: () => void;
}

function ConversationHeader({
  conversationId,
  name,
  members,
  self,
  directory,
  muted,
  onRename,
  onToggleMute,
  onBlockMember,
  onKickMember,
  groupAvatarUrl,
  onChangeAvatar,
  onLeave,
  onDeleteForEveryone,
  onExit,
  onExport,
  onStartTicTacToe,
  onStartConnectFour,
  onStartChess,
}: ConversationHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Subscribe so labels update as profiles hydrate.
  useSyncExternalStore(
    useCallback((fn) => directory.subscribe(fn), [directory]),
    () => 1,
    () => 1,
  );

  const peers = members.filter((m) => m !== self);
  const derivedName =
    peers.length === 0 ? 'Empty chat' : peers.map((m) => directory.label(m)).join(', ');
  const displayName = name ?? derivedName;

  return (
    <header className="conversation-header">
      <button
        type="button"
        className="icon"
        aria-label="back to lobby"
        title="Back to lobby"
        onClick={onExit}
        data-testid="conversation-back"
      >
        ←
      </button>
      {/* Group avatar — clickable file picker that uploads + broadcasts
          the new avatar to every member. Falls back to initials when
          no avatar is set. */}
      <label
        className="conversation-avatar-edit"
        aria-label="change group picture"
        title="Change group picture"
      >
        {groupAvatarUrl ? (
          <img
            className="avatar md"
            src={groupAvatarUrl}
            alt={displayName}
            data-testid="group-avatar"
          />
        ) : (
          <span className="avatar md" aria-hidden="true">
            {initialsFor(displayName)}
          </span>
        )}
        <span className="conversation-avatar-edit-hint" aria-hidden="true">
          📷
        </span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          data-testid="group-avatar-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onChangeAvatar(file);
            e.target.value = '';
          }}
        />
      </label>
      <div className="title-stack">
        {editing ? (
          <input
            autoFocus
            className="title-input"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onBlur={() => {
              onRename(draft.trim() || undefined);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onRename(draft.trim() || undefined);
                setEditing(false);
              } else if (e.key === 'Escape') {
                setEditing(false);
              }
            }}
          />
        ) : (
          <div className="title-row">
            <span
              className="title"
              onClick={() => {
                setDraft(name ?? '');
                setEditing(true);
              }}
              title="Click to rename"
            >
              {displayName}
            </span>
            <button
              type="button"
              className="icon title-rename-btn"
              aria-label="rename conversation"
              title="Rename"
              onClick={() => {
                setDraft(name ?? '');
                setEditing(true);
              }}
            >
              ✎
            </button>
          </div>
        )}
        <div className="subtitle member-row">
          {peers.length === 0 ? (
            <span className="faint">invite someone to get started</span>
          ) : (
            peers.map((m) => (
              <span key={m} className="member-chip">
                <span className="avatar sm" aria-hidden="true">
                  {initialsFor(directory.label(m))}
                </span>
                <span className="truncate">{directory.label(m)}</span>
                <button
                  type="button"
                  className="icon member-block-btn"
                  aria-label={`block ${directory.label(m)}`}
                  title={`Block ${directory.label(m)}`}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Block ${directory.label(m)}? You'll stop receiving their messages and invites.`,
                      )
                    ) {
                      onBlockMember(m);
                    }
                  }}
                >
                  🚫
                </button>
                <button
                  type="button"
                  className="icon member-kick-btn"
                  data-testid={`member-kick-${m}`}
                  aria-label={`kick ${directory.label(m)}`}
                  title={`Kick ${directory.label(m)} from this conversation`}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Kick ${directory.label(m)} from this conversation? They will no longer be able to send or read new messages here.`,
                      )
                    ) {
                      onKickMember(m);
                    }
                  }}
                >
                  👢
                </button>
              </span>
            ))
          )}
          <span className="faint mono" title={conversationId}>
            · {shortId(conversationId.replace(/^conv-/, ''))}
          </span>
        </div>
      </div>
      <ConversationHeaderMenu
        muted={muted}
        onToggleMute={onToggleMute}
        {...(onStartTicTacToe ? { onStartTicTacToe } : {})}
        {...(onStartConnectFour ? { onStartConnectFour } : {})}
        {...(onStartChess ? { onStartChess } : {})}
        onExport={onExport}
        onLeave={onLeave}
        onDeleteForEveryone={onDeleteForEveryone}
      />
    </header>
  );
}

interface ImageLightboxProps {
  readonly src: string;
  readonly filename: string;
  readonly onClose: () => void;
}

function ImageLightbox({ src, filename, onClose }: ImageLightboxProps) {
  // Esc closes the overlay alongside the click-outside handler.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
      onClick={onClose}
    >
      <div
        className="lightbox-actions"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <button
          type="button"
          className="ghost"
          data-testid="lightbox-download"
          onClick={() => {
            const a = document.createElement('a');
            a.href = src;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }}
        >
          ⬇ Download
        </button>
        <button
          type="button"
          className="icon"
          aria-label="close"
          data-testid="lightbox-close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <img
        className="lightbox-image"
        src={src}
        alt={filename}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </div>
  );
}

interface PinnedBarProps {
  readonly conversationId: ConversationId;
  readonly pinnedSequences: readonly number[];
  readonly messages: readonly LocalMessage[];
  readonly onJump: (sequence: number) => void;
  readonly onUnpin: (sequence: number) => void;
}

function PinnedBar({ pinnedSequences, messages, onJump, onUnpin }: PinnedBarProps) {
  const [expanded, setExpanded] = useState(false);
  // Map sequence → message for the displayed pins. We render the
  // most recent first (the pinnedSequences list is sorted ascending).
  const items = pinnedSequences
    .map((seq) => messages.find((m) => m.sequence === seq))
    .filter((m): m is LocalMessage => Boolean(m))
    .reverse();
  if (items.length === 0) return null;
  const visible = expanded ? items : items.slice(0, 1);
  return (
    <div className="pinned-bar" data-testid="pinned-bar">
      <span className="pinned-bar-label">📌 Pinned</span>
      <ul>
        {visible.map((m) => (
          <li key={m.sequence ?? -1}>
            <button
              type="button"
              className="pinned-jump"
              onClick={() => {
                if (m.sequence !== undefined) onJump(m.sequence);
              }}
              title="Jump to message"
            >
              <span className="truncate">{replyPreviewText(m)}</span>
            </button>
            <button
              type="button"
              className="icon"
              aria-label="unpin"
              title="Unpin"
              onClick={() => {
                if (m.sequence !== undefined) onUnpin(m.sequence);
              }}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      {items.length > 1 && (
        <button
          type="button"
          className="ghost pinned-toggle"
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'collapse' : `+${String(items.length - 1)} more`}
        </button>
      )}
    </div>
  );
}

interface ForwardPickerProps {
  readonly source: LocalMessage;
  readonly conversations: readonly ConversationId[];
  readonly excludeConversation?: ConversationId;
  readonly manager: ConversationManager | undefined;
  readonly self: UserId;
  readonly directory: MemberDirectory;
  readonly onCancel: () => void;
  readonly onPick: (id: ConversationId) => void;
}

function ForwardPicker({
  source,
  conversations,
  excludeConversation,
  manager,
  self,
  directory,
  onCancel,
  onPick,
}: ForwardPickerProps) {
  // Subscribe to directory updates so labels resolve as profiles
  // hydrate while the picker is open.
  useSyncExternalStore(
    useCallback((fn) => directory.subscribe(fn), [directory]),
    () => 1,
    () => 1,
  );
  const previewText = source.payload.text
    ? source.payload.text
    : source.payload.attachment
      ? source.payload.attachment.mime.startsWith('image/')
        ? '📷 image'
        : source.payload.attachment.mime.startsWith('audio/')
          ? '🎤 voice'
          : source.payload.attachment.mime.startsWith('video/')
            ? '📹 video'
            : `📎 ${source.payload.attachment.filename}`
      : '(message)';
  const targets = conversations.filter((id) => id !== excludeConversation);
  return (
    <div
      className="forward-overlay"
      role="dialog"
      aria-modal="true"
      data-testid="forward-overlay"
      onClick={onCancel}
    >
      <div
        className="forward-picker"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header>
          <h3>Forward message</h3>
          <button type="button" className="icon" aria-label="cancel forward" onClick={onCancel}>
            ✕
          </button>
        </header>
        <p className="faint forward-preview">"{previewText}"</p>
        {targets.length === 0 ? (
          <p className="empty-note">No other conversations to forward to.</p>
        ) : (
          <ul className="forward-list">
            {targets.map((id) => {
              const meta = manager?.meta(id);
              const members = manager?.members(id) ?? [];
              const peers = members.filter((m) => m !== self);
              const name =
                meta?.name ??
                (peers.length === 0
                  ? 'Empty chat'
                  : peers.map((p) => directory.label(p)).join(', '));
              // V5-LOW-04: surface destinations that include a
              // blocked user so a forward doesn't silently leak the
              // message to someone the user already chose to mute.
              // Don't hide the row — the user may still want to
              // forward intentionally.
              const blockedMembersHere = peers.filter((p) => manager?.isBlocked(p));
              return (
                <li key={id}>
                  <button
                    type="button"
                    className="use-btn"
                    data-testid={`forward-pick-${id}`}
                    onClick={() => {
                      onPick(id);
                    }}
                  >
                    <span className="avatar sm" aria-hidden="true">
                      {initialsFor(name)}
                    </span>
                    <span className="grow truncate">{name}</span>
                    {blockedMembersHere.length > 0 && (
                      <span
                        className="forward-blocked-warn"
                        data-testid={`forward-warn-${id}`}
                        title={`includes blocked: ${blockedMembersHere.map((u) => directory.label(u)).join(', ')}`}
                      >
                        🚫
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ConversationHeaderMenuProps {
  readonly muted: boolean;
  readonly onToggleMute: () => void;
  readonly onStartTicTacToe?: () => void;
  readonly onStartConnectFour?: () => void;
  readonly onStartChess?: () => void;
  readonly onExport: () => void;
  readonly onLeave: () => void;
  readonly onDeleteForEveryone: () => void;
}

function ConversationHeaderMenu({
  muted,
  onToggleMute,
  onStartTicTacToe,
  onStartConnectFour,
  onStartChess,
  onExport,
  onLeave,
  onDeleteForEveryone,
}: ConversationHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  // Close on outside-click so the popover doesn't stay open when the
  // user taps elsewhere on a phone.
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('[data-testid="conversation-menu"]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  return (
    <div className="conversation-menu" data-testid="conversation-menu">
      <button
        type="button"
        className="icon"
        data-testid="header-menu-toggle"
        aria-label="conversation actions"
        aria-expanded={open}
        title="More"
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        ⋮
      </button>
      {open && (
        <div className="conversation-menu-popover" role="menu">
          <button
            type="button"
            role="menuitem"
            data-testid="header-mute"
            onClick={() => {
              setOpen(false);
              onToggleMute();
            }}
          >
            {muted ? '🔕 Unmute' : '🔔 Mute'}
          </button>
          {onStartTicTacToe && (
            <button
              type="button"
              role="menuitem"
              data-testid="header-start-tictactoe"
              onClick={() => {
                setOpen(false);
                onStartTicTacToe();
              }}
            >
              ⭕ Start tic-tac-toe
            </button>
          )}
          {onStartConnectFour && (
            <button
              type="button"
              role="menuitem"
              data-testid="header-start-connectfour"
              onClick={() => {
                setOpen(false);
                onStartConnectFour();
              }}
            >
              🔴 Start Connect Four
            </button>
          )}
          {onStartChess && (
            <button
              type="button"
              role="menuitem"
              data-testid="header-start-chess"
              onClick={() => {
                setOpen(false);
                onStartChess();
              }}
            >
              ♞ Start Chess
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            data-testid="header-export"
            onClick={() => {
              setOpen(false);
              onExport();
            }}
          >
            ⬇ Download history
          </button>
          <hr />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              if (
                window.confirm(
                  'Leave this conversation on this device? Other members will still have it, and you will lose access to the keys.',
                )
              ) {
                onLeave();
              }
            }}
          >
            🚪 Leave
          </button>
          <button
            type="button"
            role="menuitem"
            className="danger"
            onClick={() => {
              setOpen(false);
              if (
                window.confirm(
                  'Delete this conversation for EVERY member? Server-side history and member list are wiped and every participant will lose access.',
                )
              ) {
                onDeleteForEveryone();
              }
            }}
          >
            🗑 Delete for everyone
          </button>
        </div>
      )}
    </div>
  );
}

interface MessageScrollProps {
  readonly messages: readonly LocalMessage[];
  readonly self: UserId;
  readonly directory: MemberDirectory;
  readonly hydrateAttachment: (ref: AttachmentReference) => Promise<string | undefined>;
  /**
   * Increments every time the user sends a message from the composer.
   * Forces the scroll to the bottom even if they had scrolled up —
   * "if I just typed, I want to see what I typed" trumps reading
   * history.
   */
  readonly forceScrollToken: number;
  readonly onReact: (targetSequence: number, emoji: string) => void;
  readonly onReply: (targetSequence: number) => void;
  readonly onDelete: (targetSequence: number) => void;
  readonly onForward: (message: LocalMessage) => void;
  readonly onTogglePin: (targetSequence: number, pinned: boolean) => void;
  readonly onOpenLightbox: (src: string, filename: string) => void;
  /** Set of currently-pinned sequences for this conversation. */
  readonly pinnedSequences?: ReadonlySet<number>;
  /** Per-peer read cursor, used to paint "seen" marks on own messages. */
  readonly readReceipts?: Map<UserId, number>;
  /**
   * When set, MessageScroll scrolls the matching bubble into view
   * and briefly highlights it. Wraps a non-undefined sequence + a
   * monotonic token so consecutive jumps to the same sequence still
   * re-trigger the effect.
   */
  readonly scrollTo?: { sequence: number; token: number };
}

/** Pixels from the bottom that still count as "near the bottom". */
const STICK_BOTTOM_THRESHOLD_PX = 160;

function MessageScroll({
  messages,
  self,
  directory,
  hydrateAttachment,
  forceScrollToken,
  onReact,
  onReply,
  onDelete,
  onForward,
  onTogglePin,
  onOpenLightbox,
  pinnedSequences,
  readReceipts,
  scrollTo,
}: MessageScrollProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Track whether the user is currently pinned near the bottom. */
  const stickToBottomRef = useRef(true);
  /** Sequence currently flashing as the search-jump target. */
  const [highlightedSequence, setHighlightedSequence] = useState<number | undefined>(undefined);
  useSyncExternalStore(
    useCallback((fn) => directory.subscribe(fn), [directory]),
    () => 1,
    () => 1,
  );

  // Snap to the bottom if the user is still near it. Called from
  // several places (messages change, image load, layout resize) so
  // it lives in a ref we can hand down to children without
  // re-renders.
  const scrollIfStickyRef = useRef<() => void>(() => {});
  scrollIfStickyRef.current = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  };

  // Stable callback for children: they call this on their own
  // layout-affecting events (e.g. <img> onLoad). We also retry on
  // the next animation frame because some browsers report intrinsic
  // size a tick before the layout engine has reflowed the parent.
  const onContentResize = useCallback(() => {
    scrollIfStickyRef.current();
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => scrollIfStickyRef.current());
    }
  }, []);

  // On any `messages` change — first hydrate, receive, own send —
  // scroll to bottom as long as the user is near it. Scrolling up
  // to read older messages disengages the auto-pin; scrolling back
  // within STICK_BOTTOM_THRESHOLD_PX of the bottom re-pins.
  useEffect(() => {
    scrollIfStickyRef.current();
  }, [messages]);

  // Search-jump: when the parent asks us to scroll to a specific
  // bubble, find it by data-sequence, scroll into view, and flash
  // the highlight class for 1.5s. Disengages sticky-bottom so the
  // jump doesn't immediately get yanked back.
  useEffect(() => {
    if (!scrollTo) return;
    const el = scrollRef.current;
    if (!el) return;
    const target = el.querySelector<HTMLDivElement>(
      `[data-sequence="${String(scrollTo.sequence)}"]`,
    );
    if (!target) return;
    stickToBottomRef.current = false;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedSequence(scrollTo.sequence);
    const timer = setTimeout(() => {
      setHighlightedSequence(undefined);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [scrollTo]);

  // Force-snap to the bottom when the user hits send. This overrides
  // whatever scroll position they were at — if they typed something,
  // they want to see it — and also re-engages the sticky-bottom
  // mode for subsequent receives.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [forceScrollToken]);

  // Catch layout shifts that React doesn't know about: image decode,
  // font swap, anything that grows a bubble after render. A single
  // ResizeObserver on the scroll container measures its content size
  // via `box: 'border-box'` on descendants — but that only fires for
  // the box we directly observe. To cover all descendants we also
  // wire an img onLoad callback at the leaf (see MessageBubble).
  // The observer below is a safety net that catches text reflow
  // (e.g. long links wrapping) and the scroll container's own size.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < STICK_BOTTOM_THRESHOLD_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        scrollIfStickyRef.current();
      });
      ro.observe(el);
      for (const child of Array.from(el.children)) {
        ro.observe(child);
      }
    }
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [messages.length]);

  // Pure game-control envelopes (move blobs, session-starts) have no
  // human-visible text/attachment — they drive the board in the
  // ActiveGame panel, not the chat stream. Skip them here so they
  // don't render as empty bubbles.
  const visible = messages.filter(
    (m) => m.payload.text !== undefined || m.payload.attachment !== undefined,
  );

  return (
    <div className="message-scroll" ref={scrollRef}>
      {visible.length === 0 && (
        <div
          className="faint"
          style={{
            margin: 'auto',
            textAlign: 'center',
            padding: '2em',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.4em',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: '2.4em' }}>💬</div>
          <strong style={{ color: 'var(--text-dim)' }}>No messages yet</strong>
          <span style={{ fontSize: '0.9em' }}>
            Invite someone below, then say hi. Messages are end-to-end encrypted.
          </span>
        </div>
      )}
      {visible.map((m, i) => {
        // Count peer(s) who have read up through this message's
        // sequence so the bubble can render the appropriate "seen"
        // indicator (self-only messages with known sequence).
        let readByCount = 0;
        const readByLabels: string[] = [];
        if (m.direction === 'sent' && m.sequence !== undefined && readReceipts) {
          for (const [uid, upTo] of readReceipts) {
            if (upTo >= m.sequence) {
              readByCount += 1;
              readByLabels.push(directory.label(uid));
            }
          }
        }
        return (
          <MessageBubble
            key={`${m.direction}-${String(m.at)}-${String(i)}`}
            message={m}
            self={self}
            directory={directory}
            hydrateAttachment={hydrateAttachment}
            siblings={visible}
            showSender={
              m.direction === 'received' &&
              (i === 0 || visible[i - 1]?.senderUserId !== m.senderUserId)
            }
            onContentResize={onContentResize}
            onReact={(emoji) => {
              if (m.sequence !== undefined) onReact(m.sequence, emoji);
            }}
            onReply={() => {
              if (m.sequence !== undefined) onReply(m.sequence);
            }}
            onDelete={() => {
              if (m.sequence !== undefined) onDelete(m.sequence);
            }}
            onForward={() => onForward(m)}
            onTogglePin={(pinned) => {
              if (m.sequence !== undefined) onTogglePin(m.sequence, pinned);
            }}
            onOpenLightbox={onOpenLightbox}
            isPinned={
              m.sequence !== undefined && pinnedSequences ? pinnedSequences.has(m.sequence) : false
            }
            readByCount={readByCount}
            readByLabels={readByLabels}
            highlighted={highlightedSequence !== undefined && m.sequence === highlightedSequence}
          />
        );
      })}
    </div>
  );
}

interface MessageBubbleProps {
  readonly message: LocalMessage;
  readonly self: UserId;
  readonly directory: MemberDirectory;
  readonly showSender: boolean;
  /** All currently-rendered messages, used to resolve replyToSequence → preview. */
  readonly siblings: readonly LocalMessage[];
  /** User invoked React on this bubble; parent shows the picker. */
  readonly onReact: (emoji: string) => void;
  /** User invoked Reply on this bubble; parent sets replyToSequence. */
  readonly onReply: () => void;
  /** User invoked Delete on this bubble; parent sends the deletion envelope. */
  readonly onDelete: () => void;
  /** User invoked Forward on this bubble; parent opens the conversation picker. */
  readonly onForward: () => void;
  /** Pin or unpin this message for the conversation. */
  readonly onTogglePin: (pinned: boolean) => void;
  /** Open an image attachment full-screen in the parent lightbox. */
  readonly onOpenLightbox: (src: string, filename: string) => void;
  /** Whether this message is currently pinned for the conversation. */
  readonly isPinned?: boolean;
  /** Number of peers who have read this message (only for sent msgs). */
  readonly readByCount?: number;
  /** Display labels of peers who've read, for a hover tooltip. */
  readonly readByLabels?: readonly string[];
  /** Set true while the bubble is the search-jump target (1.5s flash). */
  readonly highlighted?: boolean;
  /**
   * Called by the bubble when its own height changes in a way React
   * doesn't track (image decode, attachment URL hydration). The
   * MessageScroll container uses it to re-pin to the bottom when
   * the user is near it.
   */
  readonly onContentResize?: () => void;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥'] as const;

function MessageBubble({
  message,
  self,
  directory,
  showSender,
  siblings,
  hydrateAttachment,
  onReact,
  onReply,
  onDelete,
  onForward,
  onTogglePin,
  onOpenLightbox,
  isPinned = false,
  onContentResize,
  readByCount = 0,
  readByLabels = [],
  highlighted = false,
}: MessageBubbleProps & {
  hydrateAttachment: (ref: AttachmentReference) => Promise<string | undefined>;
}) {
  const { payload, attachmentObjectUrl, direction } = message;
  const isSelf = direction === 'sent';
  const [lazyUrl, setLazyUrl] = useState<string | undefined>(attachmentObjectUrl);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (attachmentObjectUrl) {
      setLazyUrl(attachmentObjectUrl);
      return;
    }
    if (!payload.attachment) return;
    let cancelled = false;
    void (async () => {
      const url = await hydrateAttachment(payload.attachment!);
      if (!cancelled) setLazyUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [payload.attachment, attachmentObjectUrl, hydrateAttachment]);
  const senderLabel =
    message.senderUserId && !isSelf
      ? directory.label(message.senderUserId)
      : isSelf
        ? directory.label(self)
        : undefined;

  const replyTarget =
    payload.replyToSequence !== undefined
      ? siblings.find((m) => m.sequence === payload.replyToSequence)
      : undefined;

  const isDeleted = message.deleted === true;
  const reactionEntries = Object.entries(message.reactions ?? {});
  const canDelete = isSelf && message.sequence !== undefined && !isDeleted;
  const canReact = message.sequence !== undefined && !isDeleted;
  const canReply = message.sequence !== undefined && !isDeleted;
  const canForward =
    !isDeleted && (message.payload.text !== undefined || message.payload.attachment !== undefined);
  const canPin = !isDeleted && message.sequence !== undefined;
  const canDownload = !isDeleted && payload.attachment !== undefined && lazyUrl !== undefined;

  const peerAvatarUrl =
    !isSelf && message.senderUserId ? directory.avatarUrl(message.senderUserId) : undefined;

  return (
    <div
      className={`bubble-row ${isSelf ? 'self' : 'peer'}${highlighted ? ' highlighted' : ''}`}
      data-testid={`message-${direction}`}
      {...(message.sequence !== undefined ? { 'data-sequence': String(message.sequence) } : {})}
    >
      {!isSelf &&
        (peerAvatarUrl ? (
          <img className="avatar sm" src={peerAvatarUrl} alt={senderLabel ?? ''} />
        ) : (
          <span className="avatar sm" aria-hidden="true">
            {initialsFor(senderLabel ?? '??')}
          </span>
        ))}
      <div className="bubble">
        {showSender && senderLabel && !isSelf && <span className="sender">{senderLabel}</span>}
        {payload.forwardedFrom && (
          <div className="forwarded-from" data-testid="forwarded-from">
            <span aria-hidden="true">↪</span> Forwarded from{' '}
            <strong>{directory.label(payload.forwardedFrom.senderUserId)}</strong>
          </div>
        )}
        {replyTarget && (
          <div className="reply-preview" data-testid="reply-preview">
            <span className="reply-author">
              {replyTarget.direction === 'sent'
                ? directory.label(self)
                : replyTarget.senderUserId
                  ? directory.label(replyTarget.senderUserId)
                  : '?'}
            </span>
            <span className="reply-body truncate">{replyPreviewText(replyTarget)}</span>
          </div>
        )}
        {isDeleted ? (
          <span className="faint" data-testid="message-deleted">
            message deleted
          </span>
        ) : (
          <>
            {payload.text && <span data-testid="message-text">{payload.text}</span>}
            {payload.attachment && (
              <span className="attachment" data-testid="message-attachment">
                {payload.attachment.mime.startsWith('image/') && lazyUrl ? (
                  <img
                    data-testid="attachment-image"
                    src={lazyUrl}
                    alt={payload.attachment.filename}
                    onLoad={onContentResize}
                    onClick={() => {
                      // Tap to open full-screen. The lazyUrl is the
                      // already-decrypted blob URL; the lightbox
                      // shares it directly so opening costs no
                      // extra fetch.
                      if (lazyUrl && payload.attachment) {
                        onOpenLightbox(lazyUrl, payload.attachment.filename);
                      }
                    }}
                    style={{ cursor: 'zoom-in' }}
                  />
                ) : payload.attachment.mime.startsWith('audio/') && lazyUrl ? (
                  <audio
                    data-testid="attachment-audio"
                    src={lazyUrl}
                    controls
                    preload="metadata"
                    onLoadedMetadata={onContentResize}
                  />
                ) : payload.attachment.mime.startsWith('video/') && lazyUrl ? (
                  <video
                    data-testid="attachment-video"
                    src={lazyUrl}
                    controls
                    preload="metadata"
                    playsInline
                    onLoadedMetadata={onContentResize}
                  />
                ) : (
                  <a
                    data-testid="attachment-link"
                    href={lazyUrl ?? '#'}
                    download={payload.attachment.filename}
                  >
                    {payload.attachment.filename} ({payload.attachment.size} bytes)
                  </a>
                )}
              </span>
            )}
          </>
        )}
        {reactionEntries.length > 0 && (
          <div className="reactions" data-testid="reactions">
            {reactionEntries.map(([emoji, users]) => {
              const mine = users.includes(self);
              return (
                <button
                  key={emoji}
                  type="button"
                  className={`reaction-pill${mine ? ' mine' : ''}`}
                  disabled={!canReact}
                  title={users.map((u) => directory.label(u)).join(', ')}
                  onClick={() => {
                    if (canReact) onReact(emoji);
                  }}
                >
                  <span aria-hidden="true">{emoji}</span>
                  <span className="count">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}
        <span className="timestamp" aria-hidden="true">
          {formatTime(message.at)}
          {isSelf && !isDeleted && readByCount > 0 && (
            <span
              className="read-receipt"
              data-testid="read-receipt"
              title={`Seen by ${readByLabels.join(', ')}`}
            >
              {' ✓✓'}
            </span>
          )}
        </span>
      </div>
      {(canReact || canReply || canDelete || canForward || canPin || canDownload) && (
        <div className="bubble-menu">
          <button
            className="icon"
            type="button"
            aria-label="message actions"
            title="Actions"
            onClick={() => {
              setMenuOpen((v) => !v);
            }}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="bubble-menu-popover" role="menu" data-testid="bubble-menu-popover">
              {canReact && (
                <div className="reaction-row">
                  {QUICK_REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="reaction-option"
                      onClick={() => {
                        setMenuOpen(false);
                        onReact(emoji);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              {canReply && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onReply();
                  }}
                >
                  Reply
                </button>
              )}
              {canForward && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="bubble-forward"
                  onClick={() => {
                    setMenuOpen(false);
                    onForward();
                  }}
                >
                  Forward
                </button>
              )}
              {canPin && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="bubble-toggle-pin"
                  onClick={() => {
                    setMenuOpen(false);
                    onTogglePin(!isPinned);
                  }}
                >
                  {isPinned ? '📌 Unpin' : '📌 Pin'}
                </button>
              )}
              {canDownload && (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="bubble-download"
                  onClick={() => {
                    setMenuOpen(false);
                    if (!lazyUrl || !payload.attachment) return;
                    // Synthesize a link click. The blob URL is
                    // already locally decrypted; the browser writes
                    // the file out under the original filename.
                    const a = document.createElement('a');
                    a.href = lazyUrl;
                    a.download = payload.attachment.filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                >
                  ⬇ Download
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  role="menuitem"
                  className="danger"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                >
                  Delete for everyone
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Short preview text used in the reply strip and in quoted reply
 * headers above bubbles. Falls back sensibly for attachment-only or
 * tombstoned messages.
 */
function replyPreviewText(m: LocalMessage): string {
  if (m.deleted) return '(deleted)';
  if (m.payload.text) return m.payload.text;
  if (m.payload.attachment) {
    return m.payload.attachment.mime.startsWith('image/')
      ? '📷 image'
      : `📎 ${m.payload.attachment.filename}`;
  }
  return '(message)';
}

function appendMessage(
  current: Map<ConversationId, LocalMessage[]>,
  id: ConversationId,
  message: LocalMessage,
): Map<ConversationId, LocalMessage[]> {
  const next = new Map(current);
  const arr = next.get(id) ?? [];
  next.set(id, [...arr, message]);
  return next;
}

/**
 * Replace the LocalMessage with the matching sequence number in the
 * given conversation. Used to fold reaction / deletion updates into
 * the in-memory view without touching other messages or re-sorting.
 * If the target isn't in the list yet (e.g. reaction arrived before
 * we hydrated the target, or for a sequence we never stored), this
 * is a no-op.
 */
function patchMessage(
  current: Map<ConversationId, LocalMessage[]>,
  id: ConversationId,
  sequence: number,
  updated: LocalMessage,
): Map<ConversationId, LocalMessage[]> {
  const arr = current.get(id);
  if (!arr) return current;
  const idx = arr.findIndex((m) => m.sequence === sequence);
  if (idx < 0) return current;
  const next = new Map(current);
  const replaced = arr.slice();
  const prior = replaced[idx];
  // Preserve the attachmentObjectUrl we've already materialised —
  // storedToLocal doesn't re-hydrate blobs and the updated copy
  // shouldn't lose the decrypted URL.
  replaced[idx] = prior?.attachmentObjectUrl
    ? { ...updated, attachmentObjectUrl: prior.attachmentObjectUrl }
    : updated;
  next.set(id, replaced);
  return next;
}

interface ActiveGameProps {
  readonly conversationId: ConversationId;
  readonly session: GameSessionState<unknown> | undefined;
  readonly self: UserId;
  readonly inviteTarget: UserId;
  readonly directory: MemberDirectory;
  readonly score?: GameScore;
  readonly onStartTicTacToe: (opponent: UserId | undefined) => void;
  readonly onMove: (engineId: string, move: unknown) => void;
  readonly onPlayAgain: () => void;
}

function ActiveGame({
  session,
  self,
  inviteTarget,
  directory,
  score,
  onStartTicTacToe,
  onMove,
  onPlayAgain,
}: ActiveGameProps) {
  if (session) {
    const engine = gameRegistry.get(session.engineId);
    if (!engine) return null;
    const EngineView = engine.Component;
    const terminal = engine.terminal(session.state);
    return (
      <section data-testid="active-game" className="active-game">
        <h4 data-testid="active-game-id">{engine.id}</h4>
        {score && session.players.length > 0 && (
          <div className="faint" data-testid="active-game-score" style={{ fontSize: '0.85em' }}>
            {session.players
              .map((p) => `${directory.label(p)}: ${score.winsByUser[p] ?? 0}`)
              .join(' · ')}
            {score.draws > 0 ? ` · draws: ${String(score.draws)}` : ''}
          </div>
        )}
        <EngineView
          state={session.state}
          self={self}
          players={session.players}
          onMove={(move) => {
            onMove(engine.id, move);
          }}
        />
        {terminal && (
          <button
            data-testid="play-again"
            className="ghost"
            type="button"
            onClick={onPlayAgain}
            style={{ marginTop: '0.5em' }}
          >
            play again
          </button>
        )}
      </section>
    );
  }
  return (
    <section className="active-game">
      <button
        data-testid="start-tictactoe"
        className="ghost"
        type="button"
        disabled={!inviteTarget}
        onClick={() => {
          onStartTicTacToe(inviteTarget || undefined);
        }}
      >
        start tic-tac-toe
      </button>
      <span className="faint" style={{ marginLeft: '0.5em' }}>
        {inviteTarget ? '' : '(add a player first)'}
      </span>
    </section>
  );
}

export function initialsFor(label: string): string {
  const cleaned = label.replace(/[^a-zA-Z0-9\s]/g, ' ').trim();
  if (!cleaned) return '??';
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

interface InviteBarProps {
  readonly client: ChatClient;
  readonly token: string;
  readonly self: UserId;
  readonly existingMembers: readonly UserId[];
  readonly inviteTarget: string;
  readonly onInviteTargetChange: (value: string) => void;
  readonly onInvite: (userId: string) => void;
}

function InviteBar({
  client,
  token,
  self,
  existingMembers,
  inviteTarget,
  onInviteTargetChange,
  onInvite,
}: InviteBarProps) {
  const { query, setQuery, results, error: searchError, reset } = useUserSearch(client, token);

  // Already-in-room + self filter. Matches the sidebar's behaviour
  // (don't let you re-invite yourself or someone already there).
  const existingSet = new Set<string>([self, ...existingMembers]);
  const suggestions = results.filter((r) => !existingSet.has(r.userId));

  // Render the bound search query inside the invite input so one
  // field does both. A pasted user-id bypasses search results
  // (length > 3 but not matching a prefix server-side → empty
  // results, Invite button stays enabled).
  const value = query || inviteTarget;

  return (
    <div className="invite-bar">
      <label style={{ margin: 0 }}>
        invite another user{' '}
        <span className="faint">(type a name to search, or paste a user-id)</span>
      </label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onInvite(value.trim());
          reset();
          onInviteTargetChange('');
        }}
        className="row"
      >
        <input
          data-testid="invite-input"
          value={value}
          onChange={(e) => {
            // Keep both states in lockstep so either the pasted-id
            // flow or the search flow can drive the field.
            setQuery(e.target.value);
            onInviteTargetChange(e.target.value);
          }}
          placeholder="username or user-…"
          autoComplete="off"
          spellCheck={false}
        />
        <button
          data-testid="invite-submit"
          type="submit"
          className="primary"
          disabled={!value.trim()}
        >
          invite
        </button>
      </form>
      {searchError && <p style={{ color: 'var(--danger)', margin: '0.3em 0' }}>{searchError}</p>}
      {suggestions.length > 0 && (
        <ul className="search-results invite-search-results">
          {suggestions.map((r) => {
            const label = r.displayName ?? r.username ?? `user-${shortId(r.userId)}`;
            return (
              <li key={r.userId}>
                <button
                  type="button"
                  className="use-btn"
                  data-testid={`invite-pick-${r.userId}`}
                  onClick={() => {
                    onInvite(r.userId);
                    reset();
                    onInviteTargetChange('');
                  }}
                >
                  <span className="avatar sm" aria-hidden="true">
                    {initialsFor(label)}
                  </span>
                  <span className="grow truncate">
                    <strong>{label}</strong>
                    {r.username && r.displayName && <span className="muted"> @{r.username}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {value.trim().length >= 3 &&
        suggestions.length === 0 &&
        !searchError &&
        query.trim().length >= 3 && (
          <p className="faint" style={{ margin: '0.3em 0' }}>
            no matches — paste a full user-id?
          </p>
        )}
    </div>
  );
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
